import { useState, useEffect, useMemo, useRef } from "react";
import * as d3 from "d3";
import _ from "lodash";
import Plotly from "plotly.js-dist-min";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { supabase } from "./supabaseClient";

/* ── Supabase helpers ─────────────────────────────────────────── */
async function loadTradesFromSupabase() {
  const { data, error } = await supabase
    .from("trades")
    .select("payload")
    .eq("id", 1)
    .maybeSingle();
  if (error) {
    console.error("Supabase load error:", error);
    return [];
  }
  return data?.payload || [];
}

async function saveTradestoSupabase(trades) {
  // Upsert row id=1 with full trades array
  const { error } = await supabase
    .from("trades")
    .upsert({ id: 1, payload: trades }, { onConflict: "id" });
  if (error) console.error("Supabase save error:", error);
}

/* ── Browser storage shim ─────────────────────────────────────── */
if (!window.storage) {
  window.storage = {
    async get(key) {
      const v = localStorage.getItem(key);
      return v ? { value: v } : null;
    },
    async set(key, value) {
      localStorage.setItem(key, value);
    },
    async delete(key) {
      localStorage.removeItem(key);
    },
  };
}
/* --- PWD --- */

const APP_PASSWORD = "Baktun22!";

/* ── Theme ────────────────────────────────────────────────────── */
const C = {
  bg: "#07090C",
  surface: "#0F1218",
  surface2: "#151A23",
  surfaceHover: "#1A2030",
  border: "#1C2233",
  borderLight: "#28334A",
  text: "#E4E8F0",
  textSec: "#8A95AB",
  textDim: "#4E5A72",
  profit: "#00E5B0",
  profitSoft: "rgba(0,229,176,0.12)",
  profitMid: "rgba(0,229,176,0.35)",
  loss: "#FF4D6A",
  lossSoft: "rgba(255,77,106,0.12)",
  lossMid: "rgba(255,77,106,0.35)",
  accent: "#4F8CFF",
  accentSoft: "rgba(79,140,255,0.10)",
  accentMid: "rgba(79,140,255,0.30)",
  yellow: "#FFB020",
  purple: "#A478FF",
  cyan: "#22D3EE",
};

const MULT = {
  NQ: 20,
  ES: 50,
  YM: 5,
  RTY: 50,
  CL: 1000,
  GC: 100,
  MNQ: 2,
  MES: 5,
  MYM: 0.5,
  M2K: 5,
  MCL: 100,
  MGC: 10,
  SI: 5000,
  "6E": 125000,
  ZB: 1000,
  ZN: 1000,
  NG: 10000,
  HE: 400,
  LE: 400,
  ZC: 50,
  ZS: 50,
  ZW: 50,
};
const COMM_PER_SIDE = { CL: 2.5, MNQ: 0.80 };
const DEFAULT_COMM = 2.37;
const getComm = (sym) => COMM_PER_SIDE[sym] || DEFAULT_COMM;

const DL = ["Mon", "Tue", "Wed", "Thu", "Fri"];

const MO = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const fmt = (n) => {
  if (n == null || isNaN(n)) return "—";
  return (
    (n >= 0 ? "+$" : "-$") +
    Math.abs(n)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  );
};
const fmtS = (n) => {
  if (n == null || isNaN(n)) return "—";
  return (
    (n >= 0 ? "+" : "-") +
    "$" +
    Math.abs(n)
      .toFixed(0)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  );
};
const plC = (v) => (v >= 0 ? C.profit : C.loss);

/* ── Dedup key ────────────────────────────────────────────────── */
function tradeKey(t) {
  const entryMs = t.entryTime ? new Date(t.entryTime).getTime() : "";
  const exitMs = t.exitTime ? new Date(t.exitTime).getTime() : "";
  const ep = (Number(t.entryPrice) || 0).toFixed(2);
  const xp = (Number(t.exitPrice) || 0).toFixed(2);
  return [
    t.symbol || "",
    t.contract || "",
    t.side || "",
    Number(t.qty) || 0,
    entryMs,
    exitMs,
    ep,
    xp,
  ].join("|");
}

/* ── Tradovate Parser ─────────────────────────────────────────── */

function parseTradovateOrders(csvText) {
  const lines = csvText.trim().split("\n");
  if (lines.length < 2) return [];

  const parseLine = (line) => {
    const r = [];
    let cur = "",
      inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') inQ = !inQ;
      else if (line[i] === "," && !inQ) {
        r.push(cur.trim());
        cur = "";
      } else cur += line[i];
    }
    r.push(cur.trim());
    return r;
  };

  const headers = parseLine(lines[0]);
  const ci = {};
  headers.forEach((h, i) => {
    ci[h.trim()] = i;
  });

  // Step 1: Extract all filled orders
  const fills = [];
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const c = parseLine(lines[i]);
    const status = (c[ci["Status"]] || "").trim();
    if (status !== "Filled") continue;

    const bs = (c[ci["B/S"]] || "").trim();
    const contract = (c[ci["Contract"]] || "").trim();
    const product = (c[ci["Product"]] || "").trim();
    const ftStr = (c[ci["Fill Time"]] || "").trim();
    const fq =
      parseInt(c[ci["Filled Qty"]] || c[ci["filledQty"]] || "0") ||
      parseInt(c[ci["Quantity"]] || "0") ||
      1;
    const afp =
      parseFloat(
        (c[ci["Avg Fill Price"]] || c[ci["avgPrice"]] || "0").replace(/,/g, "")
      ) || 0;

    let ft = ftStr ? new Date(ftStr) : null;
    if (ft && isNaN(ft.getTime())) ft = null;
    if (!ft) continue;

    fills.push({
      side: bs === "Buy" ? "Buy" : "Sell",
      contract,
      product,
      fillTime: ft,
      qty: fq,
      price: afp,
    });
  }

  fills.sort((a, b) => a.fillTime - b.fillTime);

  // Step 2: Group by product, build trades with per-fill FIFO P&L
  const byProd = {};
  fills.forEach((f) => {
    if (!byProd[f.product]) byProd[f.product] = [];
    byProd[f.product].push(f);
  });

  const trades = [];
  let tid = 0;

  Object.entries(byProd).forEach(([product, pf]) => {
    const m = MULT[product] || 20;

    let entryQueue = []; // FIFO: [{qty, price, time, contract}]
    let currentSide = null; // "Buy" or "Sell"

    // Accumulate for current trade
    let tradeGross = 0;
    let tradeComm = 0;
    let tradeEntryFills = [];
    let tradeExitFills = [];
    let tradeTotalEntryQty = 0;
    let tradeTotalExitQty = 0;

    pf.forEach((fill) => {
      if (currentSide === null) {
        // New position
        currentSide = fill.side;
        entryQueue = [
          {
            qty: fill.qty,
            price: fill.price,
            time: fill.fillTime,
            contract: fill.contract,
          },
        ];
        tradeEntryFills = [fill];
        tradeExitFills = [];
        tradeGross = 0;
        tradeComm = 0;
        tradeTotalEntryQty = fill.qty;
        tradeTotalExitQty = 0;
      } else if (fill.side === currentSide) {
        // Adding to position
        entryQueue.push({
          qty: fill.qty,
          price: fill.price,
          time: fill.fillTime,
          contract: fill.contract,
        });
        tradeEntryFills.push(fill);
        tradeTotalEntryQty += fill.qty;
      } else {
        // Closing (opposite side)
        tradeExitFills.push(fill);
        let rem = fill.qty;

        while (rem > 0 && entryQueue.length > 0) {
          const eq = entryQueue[0];
          const matchQty = Math.min(rem, eq.qty);

          // P&L per fill pair — no rounding loss
          const pnl =
            currentSide === "Buy"
              ? (fill.price - eq.price) * m * matchQty
              : (eq.price - fill.price) * m * matchQty;

          tradeGross += pnl;
          tradeComm += getComm(product) * 2 * matchQty;
          tradeTotalExitQty += matchQty;

          eq.qty -= matchQty;
          rem -= matchQty;
          if (eq.qty <= 0) entryQueue.shift();
        }

        // Check if position is flat
        if (entryQueue.length === 0 && rem === 0) {
          // Trade complete — emit
          const qty = tradeTotalExitQty;
          const firstEntry = tradeEntryFills[0];
          const lastExit = tradeExitFills[tradeExitFills.length - 1];

          // Weighted avg prices for display only (P&L already computed exactly)
          const avgEntry =
            tradeEntryFills.reduce((s, f) => s + f.price * f.qty, 0) /
            tradeTotalEntryQty;
          const avgExit =
            tradeExitFills.reduce((s, f) => s + f.price * f.qty, 0) /
            tradeTotalExitQty;

          trades.push({
            id: tid++,
            date: firstEntry.fillTime,
            dateStr: firstEntry.fillTime.toISOString().split("T")[0],
            entryTime: firstEntry.fillTime,
            exitTime: lastExit.fillTime,
            symbol: product,
            contract: firstEntry.contract,
            side: currentSide === "Buy" ? "Long" : "Short",
            qty,
            entryPrice: +avgEntry.toFixed(2),
            exitPrice: +avgExit.toFixed(2),
            grossPnl: tradeGross,
            commission: tradeComm,
            netPnl: tradeGross - tradeComm,
            multiplier: m,
            strategy: "",
            notes: "",
          });

          // Reset
          currentSide = null;
          entryQueue = [];
          tradeEntryFills = [];
          tradeExitFills = [];
          tradeGross = 0;
          tradeComm = 0;
          tradeTotalEntryQty = 0;
          tradeTotalExitQty = 0;
        } else if (rem > 0) {
          // Position flipped — close old, start new
          const qty = tradeTotalExitQty;
          const firstEntry = tradeEntryFills[0];

          const avgEntry =
            tradeEntryFills.reduce((s, f) => s + f.price * f.qty, 0) /
            tradeTotalEntryQty;

          trades.push({
            id: tid++,
            date: firstEntry.fillTime,
            dateStr: firstEntry.fillTime.toISOString().split("T")[0],
            entryTime: firstEntry.fillTime,
            exitTime: fill.fillTime,
            symbol: product,
            contract: firstEntry.contract,
            side: currentSide === "Buy" ? "Long" : "Short",
            qty,
            entryPrice: +avgEntry.toFixed(2),
            exitPrice: fill.price,
            grossPnl: tradeGross,
            commission: tradeComm,
            netPnl: tradeGross - tradeComm,
            multiplier: m,
            strategy: "",
            notes: "",
          });

          // Start new position with remainder
          currentSide = fill.side;
          entryQueue = [
            {
              qty: rem,
              price: fill.price,
              time: fill.fillTime,
              contract: fill.contract,
            },
          ];
          tradeEntryFills = [{ ...fill, qty: rem }];
          tradeExitFills = [];
          tradeGross = 0;
          tradeComm = 0;
          tradeTotalEntryQty = rem;
          tradeTotalExitQty = 0;
        }
      }
    });
  });

  return trades.sort((a, b) => a.entryTime - b.entryTime);
}
/* ── Sample data ──────────────────────────────────────────────── */
function genSample() {
  const trades = [];
  const cfgs = [
    { s: "NQ", m: 20, bp: 21500 },
    { s: "ES", m: 50, bp: 5900 },
  ];
  const strats = ["Breakout", "Mean Rev", "Trend", "Scalp", "Range"];
  let id = 0;
  const st = new Date(2025, 0, 6);
  for (let d = 0; d < 90; d++) {
    const dt = new Date(st);
    dt.setDate(dt.getDate() + d);
    if (dt.getDay() === 0 || dt.getDay() === 6) continue;
    const n = Math.floor(Math.random() * 5) + 1;
    for (let t = 0; t < n; t++) {
      const cfg = cfgs[Math.floor(Math.random() * cfgs.length)];
      const w = Math.random() > 0.44;
      const pts = w ? Math.random() * 25 + 2 : -(Math.random() * 15 + 1);
      const q = Math.floor(Math.random() * 3) + 1;
      const en = cfg.bp + (Math.random() - 0.5) * 200;
      const ex = en + pts;
      const g = pts * cfg.m * q;
      const cm = getComm(cfg.s) * 2 * q;
      const et = new Date(dt);
      et.setHours(
        8 + Math.floor(Math.random() * 6),
        Math.floor(Math.random() * 60)
      );
      const xt = new Date(et);
      xt.setMinutes(xt.getMinutes() + Math.floor(Math.random() * 45) + 1);
      trades.push({
        id: id++,
        date: new Date(dt),
        dateStr: dt.toISOString().split("T")[0],
        entryTime: et,
        exitTime: xt,
        symbol: cfg.s,
        contract: cfg.s + "H5",
        side: Math.random() > 0.5 ? "Long" : "Short",
        qty: q,
        entryPrice: +en.toFixed(2),
        exitPrice: +ex.toFixed(2),
        grossPnl: g,
        commission: cm,
        netPnl: g - cm,
        multiplier: cfg.m,
        strategy: strats[Math.floor(Math.random() * strats.length)],
        notes: "",
      });
    }
  }
  return trades;
}

/* ── Shared styles ────────────────────────────────────────────── */
const btnIcon = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  color: C.textSec,
  padding: "7px 9px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};
const inputSt = {
  width: "100%",
  background: C.bg,
  border: `1px solid ${C.border}`,
  borderRadius: 8,
  color: C.text,
  padding: "10px 14px",
  fontSize: 12,
  fontFamily: "var(--mono)",
  outline: "none",
  boxSizing: "border-box",
};
const cellSt = {
  padding: "7px 8px",
  fontSize: 11,
  color: C.textSec,
  borderBottom: `1px solid ${C.border}`,
  fontFamily: "var(--mono)",
  whiteSpace: "nowrap",
};
const secLabel = {
  fontSize: 11,
  fontWeight: 800,
  color: C.textDim,
  textTransform: "uppercase",
  letterSpacing: 1.2,
  marginBottom: 12,
};
const Sv = (p) => (
  <svg
    width={p.s || 17}
    height={p.s || 17}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {p.children}
  </svg>
);

/* ── Password Gate ────────────────────────────────────────────── */
function PasswordGate({ children }) {
  const KEY = "millium-journal-unlocked";
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(false);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");

  const submit = () => {
    if (pw === APP_PASSWORD) {
      setUnlocked(true);
      setErr("");
    } else setErr("Wrong password");
  };

  const logout = () => {
    try {
      localStorage.removeItem(KEY);
    } catch {}
    setUnlocked(false);
    navigate("/");
  };

  if (unlocked)
    return (
      <>
        {/* Inject logout function via context-like prop */}
        {typeof children === "function" ? children({ logout }) : children}
      </>
    );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#07090C",
        color: "#E4E8F0",
        padding: 20,
        fontFamily: "system-ui,sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          border: "1px solid #1C2233",
          borderRadius: 14,
          padding: 18,
          background: "#0F1218",
        }}
      >
        <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 10 }}>
          Journal locked
        </div>
        <input
          type="password"
          placeholder="Password"
          value={pw}
          onChange={(e) => {
            setPw(e.target.value);
            setErr("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #28334A",
            background: "#07090C",
            color: "#E4E8F0",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {err && (
          <div style={{ marginTop: 10, color: "#FF4D6A", fontWeight: 700 }}>
            {err}
          </div>
        )}
        <button
          onClick={submit}
          style={{
            marginTop: 12,
            width: "100%",
            border: "none",
            borderRadius: 10,
            padding: "10px 12px",
            fontWeight: 900,
            cursor: "pointer",
            background: "linear-gradient(135deg,#4F8CFF,#A478FF)",
            color: "#fff",
          }}
        >
          Unlock
        </button>
      </div>
    </div>
  );
}

function DayDetailModal({ dateStr, trades, onClose }) {
  const dayTrades = useMemo(() => {
    return trades
      .filter((t) => t.dateStr === dateStr)
      .sort((a, b) => a.entryTime - b.entryTime);
  }, [dateStr, trades]);

  const stats = useMemo(() => {
    if (!dayTrades.length) return null;
    const gross = dayTrades.reduce((s, t) => s + (t.grossPnl || 0), 0);
    const net = dayTrades.reduce((s, t) => s + (t.netPnl || 0), 0);
    const comm = dayTrades.reduce((s, t) => s + (t.commission || 0), 0);
    const wins = dayTrades.filter((t) => t.netPnl > 0);
    const losses = dayTrades.filter((t) => t.netPnl <= 0);
    const totalQty = dayTrades.reduce((s, t) => s + (t.qty || 0), 0);
    const grossWins = wins.reduce((s, t) => s + (t.grossPnl || 0), 0);
    const grossLosses = Math.abs(
      losses.reduce((s, t) => s + (t.grossPnl || 0), 0)
    );
    const pf =
      grossLosses > 0 ? grossWins / grossLosses : grossWins > 0 ? Infinity : 0;
    const best = dayTrades.length
      ? Math.max(...dayTrades.map((t) => t.netPnl))
      : 0;
    const worst = dayTrades.length
      ? Math.min(...dayTrades.map((t) => t.netPnl))
      : 0;

    // Intraday equity curve
    let cum = 0;
    const curve = [{ idx: 0, cum: 0, pnl: 0 }];
    dayTrades.forEach((t, i) => {
      cum += t.netPnl || 0;
      curve.push({ idx: i + 1, cum, pnl: t.netPnl });
    });

    return {
      total: dayTrades.length,
      gross,
      net,
      comm,
      wins: wins.length,
      losses: losses.length,
      winRate: dayTrades.length ? (wins.length / dayTrades.length) * 100 : 0,
      pf,
      totalQty,
      best,
      worst,
      curve,
    };
  }, [dayTrades]);

  const fmtTime = (d) => {
    if (!d) return "";
    const dt = d instanceof Date ? d : new Date(d);
    return `${String(dt.getHours()).padStart(2, "0")}:${String(
      dt.getMinutes()
    ).padStart(2, "0")}:${String(dt.getSeconds()).padStart(2, "0")}`;
  };

  const dur = (a, b) => {
    if (!a || !b) return "";
    const da = a instanceof Date ? a : new Date(a);
    const db = b instanceof Date ? b : new Date(b);
    const s = Math.floor((db - da) / 1000);
    if (s < 60) return s + "s";
    if (s < 3600) return Math.floor(s / 60) + "m " + (s % 60) + "s";
    return Math.floor(s / 3600) + "h " + Math.floor((s % 3600) / 60) + "m";
  };

  const niceDate = (() => {
    const d = new Date(dateStr + "T12:00:00");
    return d.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  })();

  if (!stats) return null;

  // Mini chart dimensions
  const chartW = 320;
  const chartH = 180;
  const curve = stats.curve;
  const maxVal = Math.max(...curve.map((c) => c.cum), 0);
  const minVal = Math.min(...curve.map((c) => c.cum), 0);
  const padY = 8;
  const toX = (i) => (i / Math.max(curve.length - 1, 1)) * chartW;
  const toY = (v) =>
    padY + ((maxVal - v) / (maxVal - minVal || 1)) * (chartH - padY * 2);
  // NEW:
  // Build green and red segments for the intraday curve
  const segments = [];
  if (curve.length > 0) {
    let segStart = 0;
    let segPositive = curve[0].cum >= 0;
    for (let i = 1; i < curve.length; i++) {
      const wasPos = curve[i - 1].cum >= 0;
      const isPos = curve[i].cum >= 0;
      if (wasPos !== isPos) {
        // Zero crossing — interpolate
        const x1 = toX(i - 1),
          y1 = curve[i - 1].cum;
        const x2 = toX(i),
          y2 = curve[i].cum;
        const ratio = Math.abs(y1) / (Math.abs(y1) + Math.abs(y2));
        const crossX = x1 + (x2 - x1) * ratio;
        const crossIdx = i - 1 + ratio;
        // Close current segment at crossing
        segments.push({
          start: segStart,
          end: i - 1,
          positive: segPositive,
          crossEnd: { x: crossX, idx: crossIdx },
        });
        // Start new segment from crossing
        segStart = i;
        segPositive = isPos;
        segments.push({
          crossStart: { x: crossX, idx: crossIdx },
          start: i,
          end: i,
          positive: segPositive,
          continuing: true,
        });
      }
    }
    // Final segment
    if (segments.length === 0) {
      segments.push({ start: 0, end: curve.length - 1, positive: segPositive });
    } else {
      const last = segments[segments.length - 1];
      last.end = curve.length - 1;
    }
  }

  // Build SVG paths per segment
  const svgSegments = segments.map((seg, si) => {
    let pathPts = [];
    if (seg.crossStart) {
      pathPts.push({ x: seg.crossStart.x, y: toY(0) });
    }
    for (let i = seg.start; i <= seg.end; i++) {
      pathPts.push({ x: toX(i), y: toY(curve[i].cum) });
    }
    if (seg.crossEnd) {
      pathPts.push({ x: seg.crossEnd.x, y: toY(0) });
    }
    const lineD = pathPts
      .map(
        (p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`
      )
      .join(" ");
    const zeroY = toY(0);
    const areaD =
      lineD +
      ` L${pathPts[pathPts.length - 1].x.toFixed(1)},${zeroY.toFixed(
        1
      )} L${pathPts[0].x.toFixed(1)},${zeroY.toFixed(1)} Z`;
    const color = seg.positive ? C.profit : C.loss;
    return { lineD, areaD, color, key: si };
  });

  // Full line path for the continuous stroke
  const pathD = curve
    .map(
      (c, i) =>
        `${i === 0 ? "M" : "L"}${toX(i).toFixed(1)},${toY(c.cum).toFixed(1)}`
    )
    .join(" ");
  const finalColor = curve.length
    ? curve[curve.length - 1].cum >= 0
      ? C.profit
      : C.loss
    : C.textDim;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.bg,
          borderRadius: 16,
          border: `1px solid ${C.border}`,
          width: "100%",
          maxWidth: 680,
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 0,
          fontFamily: "var(--sans)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 20px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, color: C.text }}>
              {niceDate}
            </div>
            <div
              style={{
                fontSize: 11,
                color: C.textDim,
                fontFamily: "var(--mono)",
                marginTop: 2,
              }}
            >
              {dateStr}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: 10,
                  color: C.textDim,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontWeight: 700,
                }}
              >
                Net P&L
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                  color: plC(stats.net),
                  fontFamily: "var(--mono)",
                }}
              >
                {fmt(stats.net)}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                color: C.textSec,
                width: 32,
                height: 32,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Humor banner */}
        {(() => {
          const n = stats.net;
          let img, label;
          if (n < 0) {
            img = "/CHASED.png";
            label = "Chased!";
          } else if (n < 1000) {
            img = "/PB.png";
            label = "Paying Dem Bills!";
          } else if (n < 10000) {
            img = "/GIN.png";
            label = "Sippin' on Gins at the Beach";
          } else {
            img = "/FFC.png";
            label = "Five Figure Club";
          }
          return (
            <div
              style={{
                textAlign: "center",
                padding: "10px 20px",
                borderBottom: `1px solid ${C.border}`,
              }}
            >
              <img
                src={img}
                alt={label}
                style={{ height: 60, borderRadius: 8, marginBottom: 4 }}
              />
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: n < 0 ? C.loss : C.profit,
                  fontFamily: "var(--mono)",
                }}
              >
                {label}
              </div>
            </div>
          );
        })()}

        <div style={{ padding: "16px 20px" }}>
          {/* Intraday Curve + Stats Row */}
          <div
            style={{
              display: "flex",
              gap: 16,
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            {/* Mini Chart */}
            <div
              style={{
                flex: "0 0 auto",
                background: C.surface,
                borderRadius: 10,
                padding: "12px 14px",
                border: `1px solid ${C.border}`,
                minHeight: 200,
              }}
            >
              <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${chartW} ${chartH}`}
                style={{ display: "block" }}
                preserveAspectRatio="none"
              >
                {/* Zero line */}
                <line
                  x1="0"
                  y1={toY(0)}
                  x2={chartW}
                  y2={toY(0)}
                  stroke={C.border}
                  strokeWidth="1"
                  strokeDasharray="4,3"
                />
                {/* Colored area fills per segment */}
                {svgSegments.map((seg) => (
                  <path
                    key={"a" + seg.key}
                    d={seg.areaD}
                    fill={seg.color}
                    fillOpacity="0.1"
                  />
                ))}
                {/* Colored line segments */}
                {svgSegments.map((seg) => (
                  <path
                    key={"l" + seg.key}
                    d={seg.lineD}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth="2"
                  />
                ))}

                {/* Dots at start/end */}
                {curve.length > 0 && (
                  <>
                    <circle
                      cx={toX(0)}
                      cy={toY(curve[0].cum)}
                      r="3"
                      fill={finalColor}
                    />
                    <circle
                      cx={toX(curve.length - 1)}
                      cy={toY(curve[curve.length - 1].cum)}
                      r="3"
                      fill={finalColor}
                    />
                  </>
                )}
                {/* Y labels */}
                <text
                  x="4"
                  y="12"
                  fill={C.textDim}
                  fontSize="9"
                  fontFamily="var(--mono)"
                >
                  {fmtS(maxVal)}
                </text>
                <text
                  x="4"
                  y={chartH - 4}
                  fill={C.textDim}
                  fontSize="9"
                  fontFamily="var(--mono)"
                >
                  {fmtS(minVal)}
                </text>
              </svg>
            </div>

            {/* Quick Stats */}
            <div
              style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                minWidth: 200,
              }}
            >
              {[
                { label: "Total Trades", value: stats.total, color: C.text },
                {
                  label: "Gross P&L",
                  value: fmtS(stats.gross),
                  color: plC(stats.gross),
                },
                {
                  label: "Winners / Losers",
                  value: `${stats.wins} / ${stats.losses}`,
                  color: C.text,
                },
                {
                  label: "Commissions",
                  value: `$${Math.abs(stats.comm).toFixed(2)}`,
                  color: C.loss,
                },
                {
                  label: "Win Rate",
                  value: `${stats.winRate.toFixed(1)}%`,
                  color: stats.winRate >= 50 ? C.profit : C.loss,
                },
                { label: "Volume", value: stats.totalQty, color: C.text },
                {
                  label: "Profit Factor",
                  value: stats.pf === Infinity ? "∞" : stats.pf.toFixed(2),
                  color: stats.pf >= 1 ? C.profit : C.loss,
                },
                {
                  label: "Peak / Bottom",
                  value: `${fmtS(
                    Math.max(...stats.curve.map((c) => c.cum))
                  )} / ${fmtS(Math.min(...stats.curve.map((c) => c.cum)))}`,
                  color: C.text,
                },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{
                    background: C.surface,
                    borderRadius: 8,
                    padding: "8px 10px",
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 8,
                      color: C.textDim,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      fontWeight: 700,
                      marginBottom: 3,
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 800,
                      color: s.color,
                      fontFamily: "var(--mono)",
                    }}
                  >
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trade List */}
          <div
            style={{
              fontSize: 9,
              color: C.textDim,
              textTransform: "uppercase",
              letterSpacing: 1.2,
              fontWeight: 800,
              marginBottom: 8,
            }}
          >
            Trades ({dayTrades.length})
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                minWidth: 580,
              }}
            >
              <thead>
                <tr>
                  {[
                    "#",
                    "Time",
                    "Symbol",
                    "Side",
                    "Qty",
                    "Entry",
                    "Exit",
                    "Gross",
                    "Net",
                    "Duration",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "6px 6px",
                        fontSize: 8,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        color: C.textDim,
                        fontWeight: 800,
                        borderBottom: `1px solid ${C.border}`,
                        whiteSpace: "nowrap",
                        background: C.surface,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dayTrades.map((t, i) => (
                  <tr
                    key={t.id || i}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = C.surfaceHover)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <td style={{ ...cellSt, fontSize: 10, color: C.textDim }}>
                      {i + 1}
                    </td>
                    <td style={cellSt}>{fmtTime(t.entryTime)}</td>
                    <td style={{ ...cellSt, fontWeight: 800, color: C.text }}>
                      {t.symbol}
                    </td>
                    <td style={cellSt}>
                      <span
                        style={{
                          padding: "2px 6px",
                          borderRadius: 4,
                          fontSize: 8,
                          fontWeight: 800,
                          background:
                            t.side === "Long" ? C.profitSoft : C.lossSoft,
                          color: t.side === "Long" ? C.profit : C.loss,
                        }}
                      >
                        {t.side}
                      </span>
                    </td>
                    <td style={cellSt}>{t.qty}</td>
                    <td style={cellSt}>{t.entryPrice?.toFixed(2)}</td>
                    <td style={cellSt}>{t.exitPrice?.toFixed(2)}</td>
                    <td
                      style={{
                        ...cellSt,
                        color: plC(t.grossPnl),
                        fontWeight: 600,
                      }}
                    >
                      {fmtS(t.grossPnl)}
                    </td>
                    <td
                      style={{
                        ...cellSt,
                        color: plC(t.netPnl),
                        fontWeight: 800,
                      }}
                    >
                      {fmt(t.netPnl)}
                    </td>
                    <td style={{ ...cellSt, color: C.textDim }}>
                      {dur(t.entryTime, t.exitTime)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── WeekDetailModal ──────────────────────────────────────────────

function WeekDetailModal({ weekDates, trades, onClose }) {
  // weekDates = array of "YYYY-MM-DD" strings (Mon-Fri of that week)
  const weekTrades = useMemo(() => {
    const ds = new Set(weekDates);
    return trades
      .filter((t) => ds.has(t.dateStr))
      .sort((a, b) => a.entryTime - b.entryTime);
  }, [weekDates, trades]);

  const stats = useMemo(() => {
    if (!weekTrades.length) return null;
    const gross = weekTrades.reduce((s, t) => s + (t.grossPnl || 0), 0);
    const net = weekTrades.reduce((s, t) => s + (t.netPnl || 0), 0);
    const comm = weekTrades.reduce((s, t) => s + (t.commission || 0), 0);
    const wins = weekTrades.filter((t) => t.netPnl > 0);
    const losses = weekTrades.filter((t) => t.netPnl <= 0);
    const totalQty = weekTrades.reduce((s, t) => s + (t.qty || 0), 0);
    const grossWins = wins.reduce((s, t) => s + (t.grossPnl || 0), 0);
    const grossLosses = Math.abs(
      losses.reduce((s, t) => s + (t.grossPnl || 0), 0)
    );
    const pf =
      grossLosses > 0 ? grossWins / grossLosses : grossWins > 0 ? Infinity : 0;

    // Weekly equity curve (cumulative across all trades)
    let cum = 0;
    const curve = [{ idx: 0, cum: 0, pnl: 0 }];
    weekTrades.forEach((t, i) => {
      cum += t.netPnl || 0;
      curve.push({ idx: i + 1, cum, pnl: t.netPnl });
    });

    // Daily breakdown
    const daily = {};
    weekTrades.forEach((t) => {
      if (!daily[t.dateStr])
        daily[t.dateStr] = {
          net: 0,
          gross: 0,
          cnt: 0,
          wins: 0,
          losses: 0,
          qty: 0,
        };
      const d = daily[t.dateStr];
      d.net += t.netPnl || 0;
      d.gross += t.grossPnl || 0;
      d.cnt += 1;
      d.qty += t.qty || 0;
      if (t.netPnl > 0) d.wins++;
      else d.losses++;
    });
    const tradingDays = Object.keys(daily).length;

    return {
      total: weekTrades.length,
      gross,
      net,
      comm,
      wins: wins.length,
      losses: losses.length,
      winRate: weekTrades.length ? (wins.length / weekTrades.length) * 100 : 0,
      pf,
      totalQty,
      curve,
      daily,
      tradingDays,
    };
  }, [weekTrades]);

  if (!stats) return null;

  // Date range label
  const sortedDates = [...weekDates].filter(Boolean).sort();
  const rangeStart = new Date(sortedDates[0] + "T12:00:00").toLocaleDateString(
    "en-US",
    { month: "short", day: "numeric" }
  );
  const rangeEnd = new Date(
    sortedDates[sortedDates.length - 1] + "T12:00:00"
  ).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const weekLabel = `${rangeStart} – ${rangeEnd}`;

  // Mini chart
  const chartW = 320;
  const chartH = 180;
  const curve = stats.curve;
  const rawMax = Math.max(...curve.map((c) => c.cum), 0);
  const rawMin = Math.min(...curve.map((c) => c.cum), 0);
  const range = rawMax - rawMin || 1;
  const maxVal = rawMax + range * 0.05;
  const minVal = rawMin - range * 0.15;
  const padY = 8;
  const toX = (i) => (i / Math.max(curve.length - 1, 1)) * chartW;
  const toY = (v) =>
    padY + ((maxVal - v) / (maxVal - minVal || 1)) * (chartH - padY * 2);

  // Build green and red segments
  const segments = [];
  if (curve.length > 0) {
    let segStart = 0;
    let segPositive = curve[0].cum >= 0;
    for (let i = 1; i < curve.length; i++) {
      const wasPos = curve[i - 1].cum >= 0;
      const isPos = curve[i].cum >= 0;
      if (wasPos !== isPos) {
        const x1 = toX(i - 1),
          y1 = curve[i - 1].cum;
        const x2 = toX(i),
          y2 = curve[i].cum;
        const ratio = Math.abs(y1) / (Math.abs(y1) + Math.abs(y2));
        const crossX = x1 + (x2 - x1) * ratio;
        const crossIdx = i - 1 + ratio;
        segments.push({
          start: segStart,
          end: i - 1,
          positive: segPositive,
          crossEnd: { x: crossX, idx: crossIdx },
        });
        segStart = i;
        segPositive = isPos;
        segments.push({
          crossStart: { x: crossX, idx: crossIdx },
          start: i,
          end: i,
          positive: segPositive,
          continuing: true,
        });
      }
    }
    if (segments.length === 0) {
      segments.push({ start: 0, end: curve.length - 1, positive: segPositive });
    } else {
      const last = segments[segments.length - 1];
      last.end = curve.length - 1;
    }
  }

  const svgSegments = segments.map((seg, si) => {
    let pathPts = [];
    if (seg.crossStart) {
      pathPts.push({ x: seg.crossStart.x, y: toY(0) });
    }
    for (let i = seg.start; i <= seg.end; i++) {
      pathPts.push({ x: toX(i), y: toY(curve[i].cum) });
    }
    if (seg.crossEnd) {
      pathPts.push({ x: seg.crossEnd.x, y: toY(0) });
    }
    const lineD = pathPts
      .map(
        (p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`
      )
      .join(" ");
    const zeroY = toY(0);
    const areaD =
      lineD +
      ` L${pathPts[pathPts.length - 1].x.toFixed(1)},${zeroY.toFixed(
        1
      )} L${pathPts[0].x.toFixed(1)},${zeroY.toFixed(1)} Z`;
    const color = seg.positive ? C.profit : C.loss;
    return { lineD, areaD, color, key: si };
  });

  const finalColor = curve.length
    ? curve[curve.length - 1].cum >= 0
      ? C.profit
      : C.loss
    : C.textDim;

  // Weekly humor
  const humor = (() => {
    const n = stats.net;
    if (n < 0) return { img: "/CHASED.png", label: "Chased!" };
    if (n >= 5000) return { img: "/MR.png", label: "Making it Rain!" };
    return null;
  })();

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.bg,
          borderRadius: 16,
          border: `1px solid ${C.border}`,
          width: "100%",
          maxWidth: 680,
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 0,
          fontFamily: "var(--sans)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 20px",
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, color: C.text }}>
              Week Summary
            </div>
            <div
              style={{
                fontSize: 11,
                color: C.textDim,
                fontFamily: "var(--mono)",
                marginTop: 2,
              }}
            >
              {weekLabel}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontSize: 10,
                  color: C.textDim,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  fontWeight: 700,
                }}
              >
                Net P&L
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                  color: plC(stats.net),
                  fontFamily: "var(--mono)",
                }}
              >
                {fmt(stats.net)}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                color: C.textSec,
                width: 32,
                height: 32,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Humor banner */}
        {humor && (
          <div
            style={{
              textAlign: "center",
              padding: "10px 20px",
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <img
              src={humor.img}
              alt={humor.label}
              style={{ height: 60, borderRadius: 8, marginBottom: 4 }}
            />
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: stats.net < 0 ? C.loss : C.profit,
                fontFamily: "var(--mono)",
              }}
            >
              {humor.label}
            </div>
          </div>
        )}

        <div style={{ padding: "16px 20px" }}>
          {/* Equity Curve + Stats Row */}
          <div
            style={{
              display: "flex",
              gap: 16,
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            {/* Mini Chart */}
            <div
              style={{
                flex: "0 0 auto",
                background: C.surface,
                borderRadius: 10,
                padding: "12px 14px",
                border: `1px solid ${C.border}`,
                minHeight: 200,
              }}
            >
              <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${chartW} ${chartH}`}
                style={{ display: "block" }}
                preserveAspectRatio="none"
              >
                <line
                  x1="0"
                  y1={toY(0)}
                  x2={chartW}
                  y2={toY(0)}
                  stroke={C.border}
                  strokeWidth="1"
                  strokeDasharray="4,3"
                />
                {svgSegments.map((seg) => (
                  <path
                    key={"a" + seg.key}
                    d={seg.areaD}
                    fill={seg.color}
                    fillOpacity="0.1"
                  />
                ))}
                {svgSegments.map((seg) => (
                  <path
                    key={"l" + seg.key}
                    d={seg.lineD}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth="2"
                  />
                ))}
                {curve.length > 0 && (
                  <>
                    <circle
                      cx={toX(0)}
                      cy={toY(curve[0].cum)}
                      r="3"
                      fill={finalColor}
                    />
                    <circle
                      cx={toX(curve.length - 1)}
                      cy={toY(curve[curve.length - 1].cum)}
                      r="3"
                      fill={finalColor}
                    />
                  </>
                )}
                <text
                  x="4"
                  y="12"
                  fill={C.textDim}
                  fontSize="9"
                  fontFamily="var(--mono)"
                >
                  {fmtS(rawMax)}
                </text>
                <text
                  x="4"
                  y={chartH - 4}
                  fill={C.textDim}
                  fontSize="9"
                  fontFamily="var(--mono)"
                >
                  {fmtS(rawMin)}
                </text>
              </svg>
            </div>

            {/* Quick Stats */}
            <div
              style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                minWidth: 200,
              }}
            >
              {[
                { label: "Total Trades", value: stats.total, color: C.text },
                {
                  label: "Gross P&L",
                  value: fmtS(stats.gross),
                  color: plC(stats.gross),
                },
                {
                  label: "Winners / Losers",
                  value: `${stats.wins} / ${stats.losses}`,
                  color: C.text,
                },
                {
                  label: "Commissions",
                  value: `$${Math.abs(stats.comm).toFixed(2)}`,
                  color: C.loss,
                },
                {
                  label: "Win Rate",
                  value: `${stats.winRate.toFixed(1)}%`,
                  color: stats.winRate >= 50 ? C.profit : C.loss,
                },
                { label: "Volume", value: stats.totalQty, color: C.text },
                {
                  label: "Profit Factor",
                  value: stats.pf === Infinity ? "∞" : stats.pf.toFixed(2),
                  color: stats.pf >= 1 ? C.profit : C.loss,
                },
                {
                  label: "Peak / Bottom",
                  value: `${fmtS(
                    Math.max(...stats.curve.map((c) => c.cum))
                  )} / ${fmtS(Math.min(...stats.curve.map((c) => c.cum)))}`,
                  color: C.text,
                },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{
                    background: C.surface,
                    borderRadius: 8,
                    padding: "8px 10px",
                    border: `1px solid ${C.border}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 8,
                      color: C.textDim,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      fontWeight: 700,
                      marginBottom: 3,
                    }}
                  >
                    {s.label}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 800,
                      color: s.color,
                      fontFamily: "var(--mono)",
                    }}
                  >
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Daily Breakdown */}
          <div
            style={{
              fontSize: 9,
              color: C.textDim,
              textTransform: "uppercase",
              letterSpacing: 1.2,
              fontWeight: 800,
              marginBottom: 8,
            }}
          >
            Daily Breakdown ({stats.tradingDays} days)
          </div>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                minWidth: 480,
              }}
            >
              <thead>
                <tr>
                  {[
                    "Day",
                    "Date",
                    "Trades",
                    "W/L",
                    "Win Rate",
                    "Gross",
                    "Net",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "6px 6px",
                        fontSize: 8,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        color: C.textDim,
                        fontWeight: 800,
                        borderBottom: `1px solid ${C.border}`,
                        whiteSpace: "nowrap",
                        background: C.surface,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.daily)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([dateStr, d], i) => {
                    const dt = new Date(dateStr + "T12:00:00");
                    const dayName = dt.toLocaleDateString("en-US", {
                      weekday: "short",
                    });
                    const dateFmt = dt.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    });
                    const wr = d.cnt
                      ? ((d.wins / d.cnt) * 100).toFixed(0)
                      : "0";
                    return (
                      <tr
                        key={dateStr}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = C.surfaceHover)
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <td
                          style={{ ...cellSt, fontWeight: 800, color: C.text }}
                        >
                          {dayName}
                        </td>
                        <td style={cellSt}>{dateFmt}</td>
                        <td style={cellSt}>{d.cnt}</td>
                        <td style={cellSt}>
                          {d.wins}/{d.losses}
                        </td>
                        <td
                          style={{
                            ...cellSt,
                            color: Number(wr) >= 50 ? C.profit : C.loss,
                            fontWeight: 600,
                          }}
                        >
                          {wr}%
                        </td>
                        <td
                          style={{
                            ...cellSt,
                            color: plC(d.gross),
                            fontWeight: 600,
                          }}
                        >
                          {fmtS(d.gross)}
                        </td>
                        <td
                          style={{
                            ...cellSt,
                            color: plC(d.net),
                            fontWeight: 800,
                          }}
                        >
                          {fmt(d.net)}
                        </td>
                      </tr>
                    );
                  })}
                {/* Week total row */}
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      ...cellSt,
                      fontWeight: 900,
                      color: C.text,
                      borderTop: `1px solid ${C.border}`,
                    }}
                  >
                    WEEK TOTAL
                  </td>
                  <td
                    style={{
                      ...cellSt,
                      color: plC(stats.gross),
                      fontWeight: 800,
                      borderTop: `1px solid ${C.border}`,
                    }}
                  >
                    {fmtS(stats.gross)}
                  </td>
                  <td
                    style={{
                      ...cellSt,
                      color: plC(stats.net),
                      fontWeight: 900,
                      borderTop: `1px solid ${C.border}`,
                    }}
                  >
                    {fmt(stats.net)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Calendar Heatmap (Sunday start, no Saturday, weekly totals) ── */

function CalendarHeatmap({
  trades,
  year,
  month,
  onMonthChange,
  onDayClick,
  onWeekClick,
}) {
  const dailyPnl = useMemo(() => {
    const m = {};
    trades.forEach((t) => {
      const d = new Date(t.dateStr + "T12:00:00");
      let ds = t.dateStr;
      if (d.getDay() === 0) {
        const mon = new Date(d);
        mon.setDate(mon.getDate() + 1);
        ds = mon.toISOString().slice(0, 10);
      }
      m[ds] = (m[ds] || 0) + t.netPnl;
    });
    return m;
  }, [trades]);
  const dailyCounts = useMemo(() => {
    const m = {};
    trades.forEach((t) => {
      const d = new Date(t.dateStr + "T12:00:00");
      let ds = t.dateStr;
      if (d.getDay() === 0) {
        const mon = new Date(d);
        mon.setDate(mon.getDate() + 1);
        ds = mon.toISOString().slice(0, 10);
      }
      m[ds] = (m[ds] || 0) + 1;
    });
    return m;
  }, [trades]);

  const dim = new Date(year, month + 1, 0).getDate();
  // Find the first weekday (Mon-Fri) of the month
  let firstWeekday = 1;
  while (firstWeekday <= dim) {
    const dow = new Date(year, month, firstWeekday).getDay();
    if (dow >= 1 && dow <= 5) break; // Mon=1..Fri=5
    firstWeekday++;
  }
  // Offset: which column does the first weekday land in? (Mon=0, Tue=1...Fri=4)
  const fd = new Date(year, month, firstWeekday).getDay() - 1; // Mon=0, Tue=1...Fri=4

  // Build weeks (Mon-Fri, skip Sat+Sun)
  const weeks = [];
  let wk = new Array(fd).fill(null);

  for (let d = 1; d <= dim; d++) {
    const dow = new Date(year, month, d).getDay();
    if (dow === 0 || dow === 6) continue; // skip Saturday and Sunday
    wk.push(d);
    if (wk.length === 5) {
      weeks.push(wk);
      wk = [];
    }
  }
  if (wk.length) {
    while (wk.length < 5) wk.push(null);
    weeks.push(wk);
  }

  const vals = Object.values(dailyPnl);
  const maxAbs = Math.max(d3.max(vals, (v) => Math.abs(v)) || 100, 50);
  const cc = (v) => {
    if (v === 0) return C.border;
    const t = Math.min(Math.abs(v) / maxAbs, 1);
    return v > 0
      ? `rgba(0,229,176,${0.15 + t * 0.65})`
      : `rgba(255,77,106,${0.15 + t * 0.65})`;
  };

  const mk = `${year}-${String(month + 1).padStart(2, "0")}`;
  const mT = Object.entries(dailyPnl)
    .filter(([k]) => k.startsWith(mk))
    .reduce((s, [, v]) => s + v, 0);
  const mTr = Object.entries(dailyCounts)
    .filter(([k]) => k.startsWith(mk))
    .reduce((s, [, v]) => s + v, 0);
  const mD = Object.entries(dailyPnl).filter(([k]) => k.startsWith(mk)).length;
  const mW = Object.entries(dailyPnl).filter(
    ([k, v]) => k.startsWith(mk) && v > 0
  ).length;

  // Compute weekly totals
  const weeklyTotals = weeks.map((wk) => {
    let total = 0;
    wk.forEach((day) => {
      if (day === null) return;
      const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`;
      total += dailyPnl[ds] || 0;
    });
    return total;
  });

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <button onClick={() => onMonthChange(-1)} style={btnIcon}>
          <Sv>
            <polyline points="15 18 9 12 15 6" />
          </Sv>
        </button>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: 17,
              fontWeight: 800,
              color: C.text,
              fontFamily: "var(--mono)",
            }}
          >
            {MO[month]} {year}
          </div>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              marginTop: 4,
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: plC(mT),
                fontFamily: "var(--mono)",
              }}
            >
              {fmt(mT)}
            </span>
            <span
              style={{
                fontSize: 11,
                color: C.textDim,
                fontFamily: "var(--mono)",
              }}
            >
              {mTr}t · {mD}d · {mW}W
            </span>
          </div>
        </div>
        <button onClick={() => onMonthChange(1)} style={btnIcon}>
          <Sv>
            <polyline points="9 18 15 12 9 6" />
          </Sv>
        </button>
      </div>

      {/* Day headers: Sun-Fri + Weekly total */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5,1fr) 0.5fr",
          gap: 3,
          marginBottom: 3,
        }}
      >
        {DL.map((d) => (
          <div
            key={d}
            style={{
              textAlign: "center",
              fontSize: 9,
              color: C.textDim,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase",
              padding: 2,
            }}
          >
            {d}
          </div>
        ))}
        <div
          style={{
            textAlign: "center",
            fontSize: 9,
            color: C.yellow,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
            padding: 2,
          }}
        >
          WK
        </div>
      </div>

      {/* Weeks */}
      {weeks.map((wk, wi) => (
        <div
          key={wi}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5,1fr) 0.5fr",
            gap: 3,
            marginBottom: 3,
          }}
        >
          {wk.map((day, di) => {
            if (day === null) return <div key={di} />;
            const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(
              day
            ).padStart(2, "0")}`;
            const pnl = dailyPnl[ds] || 0;
            const cnt = dailyCounts[ds] || 0;
            const has = cnt > 0;
            return (
              <div
                key={di}
                onClick={() => has && onDayClick && onDayClick(ds)}
                style={{
                  aspectRatio: "1.5",
                  cursor: has ? "pointer" : "default",
                  borderRadius: 8,
                  background: has ? cc(pnl) : C.surface,
                  border: `1px solid ${has ? "transparent" : C.border}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 36,
                  overflow: "hidden",
                  padding: 2,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: has ? C.text : C.textDim,
                    fontFamily: "var(--mono)",
                  }}
                >
                  {day}
                </span>
                {has && (
                  <>
                    <span
                      style={{
                        fontSize: "clamp(7px,2.2vw,12px)",
                        fontWeight: 800,
                        color: plC(pnl),
                        fontFamily: "var(--mono)",
                        marginTop: 1,
                      }}
                    >
                      {fmtS(pnl)}
                    </span>
                    <span
                      style={{
                        fontSize: 7,
                        color: "rgba(255,255,255,0.35)",
                        fontWeight: 600,
                      }}
                    >
                      {cnt}t
                    </span>
                  </>
                )}
              </div>
            );
          })}
          {/* Weekly total */}

          <div
            onClick={() => {
              if (weeklyTotals[wi] !== 0 && onWeekClick) {
                const dates = wk
                  .filter((d) => d !== null)
                  .map(
                    (d) =>
                      `${year}-${String(month + 1).padStart(2, "0")}-${String(
                        d
                      ).padStart(2, "0")}`
                  );
                onWeekClick(dates);
              }
            }}
            style={{
              borderRadius: 8,
              cursor: weeklyTotals[wi] !== 0 ? "pointer" : "default",
              background:
                weeklyTotals[wi] !== 0
                  ? weeklyTotals[wi] > 0
                    ? C.profitSoft
                    : C.lossSoft
                  : C.surface,
              border: `1px solid ${
                weeklyTotals[wi] !== 0 ? "transparent" : C.border
              }`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 36,
              padding: 2,
            }}
          >
            <span
              style={{
                fontSize: "clamp(7px,2vw,11px)",
                fontWeight: 800,
                color:
                  weeklyTotals[wi] !== 0 ? plC(weeklyTotals[wi]) : C.textDim,
                fontFamily: "var(--mono)",
              }}
            >
              {weeklyTotals[wi] !== 0 ? fmtS(weeklyTotals[wi]) : "—"}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Equity Curve ─────────────────────────────────────────────── */
function EquityCurve({ trades }) {
  const ref = useRef(null);
  const data = useMemo(() => {
    const sorted = [...trades].sort((a, b) => a.entryTime - b.entryTime);
    const dm = {};
    sorted.forEach((t) => {
      dm[t.dateStr] = (dm[t.dateStr] || 0) + t.netPnl;
    });
    let cum = 0;
    return Object.entries(dm)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([d, p]) => {
        cum += p;
        return { date: d, cum, pnl: p };
      });
  }, [trades]);
  useEffect(() => {
    if (!ref.current || data.length < 2) return;
    const final = data[data.length - 1].cum;
    const lc = final >= 0 ? C.profit : C.loss;
    const fc = final >= 0 ? "rgba(0,229,176,0.08)" : "rgba(255,77,106,0.08)";
    Plotly.newPlot(
      ref.current,
      [
        {
          x: data.map((d) => d.date),
          y: data.map((d) => d.cum),
          customdata: data.map((d) => d.pnl),
          type: "scatter",
          mode: "lines",
          fill: "tozeroy",
          fillcolor: fc,
          line: { color: lc, width: 2.5, shape: "spline" },
          hovertemplate:
            "%{x}<br>Cumulative: $%{y:,.2f}<br>Day P&L: $%{customdata:,.2f}<extra></extra>",
        },
      ],
      {
        paper_bgcolor: "transparent",
        plot_bgcolor: "transparent",
        font: {
          family: "'JetBrains Mono',monospace",
          color: C.textDim,
          size: 10,
        },
        margin: { l: 55, r: 15, t: 10, b: 35 },
        xaxis: {
          gridcolor: C.border,
          linecolor: C.border,
          tickfont: { size: 9 },
        },
        yaxis: {
          gridcolor: C.border,
          linecolor: C.border,
          tickprefix: "$",
          tickfont: { size: 9 },
          zerolinecolor: C.textDim,
          zerolinewidth: 1,
        },
        hovermode: "x unified",
        hoverlabel: {
          bgcolor: C.surface2,
          bordercolor: C.border,
          font: { family: "'JetBrains Mono',monospace", size: 11 },
        },
      },
      { responsive: true, displayModeBar: false }
    );
    return () => {
      if (ref.current) Plotly.purge(ref.current);
    };
  }, [data]);
  if (data.length < 2)
    return (
      <div style={{ color: C.textSec, textAlign: "center", padding: 40 }}>
        Need 2+ trading days
      </div>
    );
  return <div ref={ref} style={{ width: "100%", height: 280 }} />;
}

/* ── Performance Chart (NT-style multi-mode) ─────────────────── */
function PerformanceChart({ trades }) {
  const [chartPeriod, setChartPeriod] = useState("daily");
  const [chartType, setChartType] = useState("cumNet");
  const plotRef = useRef(null);

  const chartData = useMemo(() => {
    const sorted = [...trades].sort(
      (a, b) => new Date(a.entryTime) - new Date(b.entryTime)
    );
    if (!sorted.length) return [];

    const groups = {};
    sorted.forEach((t, idx) => {
      // Remap Sunday to Monday (same as calendar)
      let effectiveDate = t.dateStr;
      const dateObj = new Date(t.dateStr + "T12:00:00");
      if (dateObj.getDay() === 0) {
        const mon = new Date(dateObj);
        mon.setDate(mon.getDate() + 1);
        effectiveDate = mon.toISOString().slice(0, 10);
      }

      let key;
      if (chartPeriod === "trade") {
        key = idx;
      } else if (chartPeriod === "daily") {
        key = effectiveDate;
      } else if (chartPeriod === "weekly") {
        const d = new Date(effectiveDate + "T12:00:00");
        const day = d.getDay();
        const mon = new Date(d);
        mon.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
        key = mon.toISOString().split("T")[0];
      } else {
        key = effectiveDate.slice(0, 7);
      }
      if (!groups[key])
        groups[key] = {
          pnl: 0,
          label: chartPeriod === "trade" ? `#${idx + 1}` : String(key),
        };
      groups[key].pnl += t.netPnl || 0;
    });

    const entries = Object.values(groups);
    let cum = 0,
      peak = 0,
      maxDD = 0;
    return entries.map((e) => {
      cum += e.pnl;
      if (cum > peak) peak = cum;
      const dd = peak - cum;
      if (dd > maxDD) maxDD = dd;
      return { label: e.label, pnl: e.pnl, cum, dd, cumDD: maxDD };
    });
  }, [trades, chartPeriod]);

  useEffect(() => {
    if (!plotRef.current || chartData.length < 2) return;
    const labels = chartData.map((d) => d.label);
    let yVals,
      isBars = false,
      color,
      fillColor,
      hoverTpl;

    if (chartType === "cumNet") {
      yVals = chartData.map((d) => d.cum);
      const final = yVals[yVals.length - 1] || 0;
      color = final >= 0 ? C.profit : C.loss;
      fillColor = final >= 0 ? "rgba(0,229,176,0.08)" : "rgba(255,77,106,0.08)";
      hoverTpl = "%{x}<br>Cumulative: $%{y:,.2f}<extra></extra>";
    } else if (chartType === "net") {
      yVals = chartData.map((d) => d.pnl);
      isBars = true;
      hoverTpl = "%{x}<br>P&L: $%{y:,.2f}<extra></extra>";
    } else if (chartType === "cumDD") {
      yVals = chartData.map((d) => -d.dd);
      color = C.loss;
      fillColor = "rgba(255,77,106,0.12)";
      hoverTpl = "%{x}<br>Drawdown: $%{y:,.2f}<extra></extra>";
    } else {
      yVals = chartData.map((d) => -d.dd);
      isBars = true;
      hoverTpl = "%{x}<br>Drawdown: $%{y:,.2f}<extra></extra>";
    }

    const trace = isBars
      ? {
          x: labels,
          y: yVals,
          type: "bar",
          marker: { color: yVals.map((v) => (v >= 0 ? C.profit : C.loss)) },
          hovertemplate: hoverTpl,
        }
      : {
          x: labels,
          y: yVals,
          type: "scatter",
          mode: "lines",
          fill: "tozeroy",
          fillcolor: fillColor,
          line: { color, width: 2.5, shape: "spline" },
          hovertemplate: hoverTpl,
        };

    Plotly.newPlot(
      plotRef.current,
      [trace],
      {
        paper_bgcolor: "transparent",
        plot_bgcolor: "transparent",
        font: {
          family: "'JetBrains Mono',monospace",
          color: C.textDim,
          size: 10,
        },
        margin: { l: 55, r: 15, t: 10, b: 35 },
        xaxis: {
          gridcolor: C.border,
          linecolor: C.border,
          tickfont: { size: 9 },
        },
        yaxis: {
          gridcolor: C.border,
          linecolor: C.border,
          tickprefix: "$",
          tickfont: { size: 9 },
          zerolinecolor: C.textDim,
          zerolinewidth: 1,
        },
        hovermode: "x unified",
        hoverlabel: {
          bgcolor: C.surface2,
          bordercolor: C.border,
          font: { family: "'JetBrains Mono',monospace", size: 11 },
        },
        bargap: 0.3,
      },
      { responsive: true, displayModeBar: false }
    );

    return () => {
      if (plotRef.current) Plotly.purge(plotRef.current);
    };
  }, [chartData, chartType]);

  const selStyle = {
    background: C.bg,
    color: C.text,
    border: `1px solid ${C.borderLight}`,
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 11,
    fontFamily: "var(--mono)",
    fontWeight: 600,
    outline: "none",
    cursor: "pointer",
  };

  const typeOpts = [
    { value: "cumNet", label: "Cumulative Net Profit" },
    { value: "net", label: "Net Profit" },
    { value: "cumDD", label: "Cumulative Max Drawdown" },
    { value: "dd", label: "Max Drawdown" },
  ];
  const periodOpts = [
    { value: "trade", label: "Trade" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
  ];

  return (
    <div
      style={{
        background: C.surface,
        borderRadius: 12,
        padding: 14,
        border: `1px solid ${C.border}`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: C.textDim,
            textTransform: "uppercase",
            letterSpacing: 1.2,
          }}
        >
          Performance Chart
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <select
            value={chartPeriod}
            onChange={(e) => setChartPeriod(e.target.value)}
            style={selStyle}
          >
            {periodOpts.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
            style={selStyle}
          >
            {typeOpts.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {chartData.length < 2 ? (
        <div style={{ color: C.textSec, textAlign: "center", padding: 40 }}>
          Need more data
        </div>
      ) : (
        <div ref={plotRef} style={{ width: "100%", height: 300 }} />
      )}
    </div>
  );
}

/* ── Daily Bars ───────────────────────────────────────────────── */
function DailyBars({ trades }) {
  const ref = useRef(null);
  const data = useMemo(() => {
    const dm = {};
    trades.forEach((t) => {
      dm[t.dateStr] = (dm[t.dateStr] || 0) + t.netPnl;
    });
    return Object.entries(dm)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([d, p]) => ({ date: d, pnl: p }));
  }, [trades]);
  useEffect(() => {
    if (!ref.current || data.length < 1) return;
    Plotly.newPlot(
      ref.current,
      [
        {
          x: data.map((d) => d.date),
          y: data.map((d) => d.pnl),
          type: "bar",
          marker: {
            color: data.map((d) => (d.pnl >= 0 ? C.profit : C.loss)),
            opacity: 0.8,
          },
          hovertemplate: "%{x}<br>$%{y:,.2f}<extra></extra>",
        },
      ],
      {
        paper_bgcolor: "transparent",
        plot_bgcolor: "transparent",
        font: {
          family: "'JetBrains Mono',monospace",
          color: C.textDim,
          size: 10,
        },
        margin: { l: 55, r: 15, t: 10, b: 35 },
        xaxis: {
          gridcolor: "transparent",
          linecolor: C.border,
          tickfont: { size: 9 },
        },
        yaxis: {
          gridcolor: C.border,
          linecolor: C.border,
          tickprefix: "$",
          tickfont: { size: 9 },
          zerolinecolor: C.textDim,
        },
        bargap: 0.3,
        hovermode: "x unified",
        hoverlabel: {
          bgcolor: C.surface2,
          bordercolor: C.border,
          font: { family: "'JetBrains Mono',monospace", size: 11 },
        },
      },
      { responsive: true, displayModeBar: false }
    );
    return () => {
      if (ref.current) Plotly.purge(ref.current);
    };
  }, [data]);
  if (data.length < 1) return null;
  return <div ref={ref} style={{ width: "100%", height: 220 }} />;
}

/* ── Strategy Breakdown ───────────────────────────────────────── */
function StrategyBreakdown({ trades }) {
  const data = useMemo(() => {
    const m = {};
    trades.forEach((t) => {
      const s = t.strategy || "Untagged";
      if (!m[s]) m[s] = { name: s, pnl: 0, count: 0, wins: 0 };
      m[s].pnl += t.netPnl;
      m[s].count++;
      if (t.netPnl > 0) m[s].wins++;
    });
    return Object.values(m).sort((a, b) => b.pnl - a.pnl);
  }, [trades]);
  const colors = [
    "#4F8CFF",
    "#00E5B0",
    "#FFB020",
    "#A478FF",
    "#FF4D6A",
    "#22D3EE",
    "#EC4899",
  ];
  const maxC = Math.max(1, ...data.map((d) => d.count));
  return (
    <div>
      {data.map((s, i) => {
        const wr = (s.wins / s.count) * 100;
        const bw = Math.min(100, Math.max(5, (s.count / maxC) * 100));
        return (
          <div
            key={s.name}
            style={{
              padding: "10px 0",
              borderBottom:
                i < data.length - 1 ? `1px solid ${C.border}` : "none",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 6,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: colors[i % colors.length],
                  }}
                />
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  {s.name}
                </span>
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: plC(s.pnl),
                  fontFamily: "var(--mono)",
                }}
              >
                {fmt(s.pnl)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                gap: 16,
                fontSize: 11,
                color: C.textSec,
                fontFamily: "var(--mono)",
              }}
            >
              <span>{s.count} trades</span>
              <span style={{ color: wr >= 50 ? C.profit : C.loss }}>
                {wr.toFixed(0)}% WR
              </span>
              <span>avg {fmt(s.pnl / s.count)}</span>
            </div>
            <div
              style={{
                height: 4,
                borderRadius: 2,
                background: C.border,
                marginTop: 6,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${bw}%`,
                  borderRadius: 2,
                  background: colors[i % colors.length],
                  opacity: 0.7,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Stat Card ────────────────────────────────────────────────── */
function Stat({ label, value, sub, color }) {
  return (
    <div
      style={{
        background: C.surface,
        borderRadius: 10,
        padding: "12px 14px",
        border: `1px solid ${C.border}`,
        flex: "1 1 130px",
        minWidth: 120,
      }}
    >
      <div
        style={{
          fontSize: 9,
          color: C.textDim,
          textTransform: "uppercase",
          letterSpacing: 1.3,
          fontWeight: 700,
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: color || C.text,
          fontFamily: "var(--mono)",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          style={{
            fontSize: 10,
            color: C.textSec,
            marginTop: 3,
            fontFamily: "var(--mono)",
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

/* ── Trade Log ────────────────────────────────────────────────── */
function TradeLog({ trades, onUpdate }) {
  const [filter, setFilter] = useState("");
  const sorted = useMemo(() => {
    let list = [...trades];
    if (filter) {
      const f = filter.toLowerCase();
      list = list.filter(
        (t) =>
          (t.symbol || "").toLowerCase().includes(f) ||
          (t.strategy || "").toLowerCase().includes(f) ||
          (t.dateStr || "").includes(f) ||
          (t.side || "").toLowerCase().includes(f)
      );
    }
    return list.sort((a, b) => b.entryTime - a.entryTime);
  }, [trades, filter]);
  const fmtTime = (d) => {
    if (!d) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(
      d.getMinutes()
    ).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
  };
  const dur = (a, b) => {
    if (!a || !b) return "";
    const s = Math.floor((b - a) / 1000);
    if (s < 60) return s + "s";
    if (s < 3600) return Math.floor(s / 60) + "m " + (s % 60) + "s";
    return Math.floor(s / 3600) + "h " + Math.floor((s % 3600) / 60) + "m";
  };
  return (
    <div>
      <input
        type="text"
        placeholder="Filter: symbol, strategy, date, side..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={inputSt}
      />
      <div style={{ overflowX: "auto", marginTop: 10 }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            minWidth: 720,
          }}
        >
          <thead>
            <tr>
              {[
                "Date",
                "Time",
                "Symbol",
                "Side",
                "Qty",
                "Entry",
                "Exit",
                "Gross",
                "Net",
                "Duration",
                "Strategy",
              ].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "7px 8px",
                    fontSize: 9,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    color: C.textDim,
                    fontWeight: 800,
                    borderBottom: `1px solid ${C.border}`,
                    whiteSpace: "nowrap",
                    position: "sticky",
                    top: 0,
                    background: C.surface,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0, 200).map((t) => (
              <tr
                key={t.id}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = C.surfaceHover)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <td style={cellSt}>{t.dateStr}</td>
                <td style={cellSt}>{fmtTime(t.entryTime)}</td>
                <td style={{ ...cellSt, fontWeight: 800, color: C.text }}>
                  {t.symbol}
                </td>
                <td style={cellSt}>
                  <span
                    style={{
                      padding: "2px 7px",
                      borderRadius: 4,
                      fontSize: 9,
                      fontWeight: 800,
                      background: t.side === "Long" ? C.profitSoft : C.lossSoft,
                      color: t.side === "Long" ? C.profit : C.loss,
                    }}
                  >
                    {t.side}
                  </span>
                </td>
                <td style={cellSt}>{t.qty}</td>
                <td style={cellSt}>{t.entryPrice?.toFixed(2)}</td>
                <td style={cellSt}>{t.exitPrice?.toFixed(2)}</td>
                <td
                  style={{ ...cellSt, color: plC(t.grossPnl), fontWeight: 600 }}
                >
                  {fmtS(t.grossPnl)}
                </td>
                <td
                  style={{ ...cellSt, color: plC(t.netPnl), fontWeight: 800 }}
                >
                  {fmt(t.netPnl)}
                </td>
                <td style={{ ...cellSt, color: C.textDim }}>
                  {dur(t.entryTime, t.exitTime)}
                </td>
                <td style={cellSt}>
                  <input
                    value={t.strategy}
                    onChange={(e) => onUpdate(t.id, "strategy", e.target.value)}
                    placeholder="+ tag"
                    style={{
                      background: "transparent",
                      border: `1px solid ${C.border}`,
                      borderRadius: 4,
                      color: C.text,
                      padding: "3px 6px",
                      fontSize: 10,
                      width: 80,
                      fontFamily: "var(--mono)",
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sorted.length > 200 && (
          <div
            style={{
              textAlign: "center",
              color: C.textSec,
              padding: 14,
              fontSize: 11,
            }}
          >
            Showing 200 of {sorted.length}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Import Modal ─────────────────────────────────────────────── */
function ImportModal({ onImport, onClose }) {
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const processFile = (file) => {
    setError(null);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const trades = parseTradovateOrders(e.target.result);
        if (trades.length === 0) {
          setError("No filled trades found.");
          return;
        }
        setResult(trades);
      } catch (err) {
        setError("Parse error: " + err.message);
      }
    };
    reader.readAsText(file);
  };
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
        backdropFilter: "blur(6px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: C.surface,
          borderRadius: 16,
          padding: 28,
          maxWidth: 460,
          width: "100%",
          border: `1px solid ${C.border}`,
          maxHeight: "90vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h2
            style={{ fontSize: 17, fontWeight: 800, color: C.text, margin: 0 }}
          >
            Import Tradovate Orders
          </h2>
          <button onClick={onClose} style={{ ...btnIcon, padding: 5 }}>
            <Sv s={16}>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </Sv>
          </button>
        </div>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0]);
          }}
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? C.accent : C.borderLight}`,
            borderRadius: 12,
            padding: "36px 20px",
            textAlign: "center",
            cursor: "pointer",
            background: dragOver ? C.accentSoft : "transparent",
            transition: "all 0.2s",
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            hidden
            onChange={(e) => {
              if (e.target.files[0]) processFile(e.target.files[0]);
            }}
          />
          <Sv s={24}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </Sv>
          <div style={{ marginTop: 10, color: C.text, fontWeight: 700 }}>
            Drop your Orders.csv here
          </div>
          <div style={{ marginTop: 4, color: C.textSec, fontSize: 12 }}>
            or click to browse
          </div>
        </div>
        {error && (
          <div
            style={{
              marginTop: 14,
              padding: 12,
              background: C.lossSoft,
              borderRadius: 8,
              border: `1px solid ${C.lossMid}`,
            }}
          >
            <div style={{ fontSize: 12, color: C.loss, fontWeight: 600 }}>
              {error}
            </div>
          </div>
        )}
        {result && (
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                padding: 14,
                background: C.profitSoft,
                borderRadius: 8,
                border: `1px solid ${C.profitMid}`,
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 13, color: C.profit, fontWeight: 700 }}>
                ✓ {result.length} trades reconstructed
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: C.textSec,
                  marginTop: 4,
                  fontFamily: "var(--mono)",
                }}
              >
                {[...new Set(result.map((t) => t.symbol))].join(", ")} ·{" "}
                {[...new Set(result.map((t) => t.dateStr))].length} days · Net:{" "}
                <span
                  style={{
                    color: plC(result.reduce((s, t) => s + t.netPnl, 0)),
                    fontWeight: 700,
                  }}
                >
                  {fmt(result.reduce((s, t) => s + t.netPnl, 0))}
                </span>
              </div>
            </div>
            <button
              onClick={() => onImport(result)}
              style={{
                width: "100%",
                background: `linear-gradient(135deg,${C.accent},${C.purple})`,
                border: "none",
                borderRadius: 10,
                color: "#fff",
                padding: "12px 20px",
                fontSize: 14,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Import {result.length} Trades
            </button>
          </div>
        )}
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: C.bg,
            borderRadius: 8,
            border: `1px solid ${C.border}`,
          }}
        >
          <div
            style={{
              fontSize: 10,
              color: C.textSec,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 1,
              marginBottom: 6,
            }}
          >
            How it works
          </div>
          <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.7 }}>
            Parses Tradovate "Orders" CSV. Filters filled orders, matches
            entries/exits via FIFO, computes P&L with contract multipliers
            (NQ=$20/pt, ES=$50/pt) and $2.37/side commission ($2.50 for CL).
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Ticker Tape ──────────────────────────────────────────────── */
function TickerTape() {
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const tape = document.createElement("tv-ticker-tape");
    tape.setAttribute(
      "symbols",
      "NASDAQ:QQQ,AMEX:SPY,TVC:GOLD,BITSTAMP:BTCUSD,NASDAQ:TSLA,NASDAQ:AAPL,NYSE:BRK.B,NASDAQ:NVDA,NASDAQ:GOOGL,FX:USDMXN,TICKMILL:EURUSD"
    );
    tape.setAttribute("item-size", "compact");
    tape.setAttribute("theme", "dark");
    containerRef.current.appendChild(tape);
    const script = document.createElement("script");
    script.type = "module";
    script.src =
      "https://widgets.tradingview-widget.com/w/en/tv-ticker-tape.js";
    document.head.appendChild(script);
    return () => {
      script.remove();
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, []);
  return <div ref={containerRef} />;
}

/* ── Public Section ───────────────────────────────────────────── */
function PublicSection({ trades }) {
  const [mode, setMode] = useState("last20");
  const [anchorDate, setAnchorDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [pubCalMonth, setPubCalMonth] = useState(() => new Date().getMonth());
  const [pubCalYear, setPubCalYear] = useState(() => new Date().getFullYear());
  useEffect(() => {
    if (trades.length) {
      const sorted = [...trades]
        .filter((t) => t.dateStr)
        .sort((a, b) => a.dateStr.localeCompare(b.dateStr));
      const latest = sorted[sorted.length - 1];
      if (latest?.dateStr) {
        const [y, m] = latest.dateStr.split("-").map(Number);
        if (y && m) {
          setPubCalYear(y);
          setPubCalMonth(m - 1);
        }
      }
    }
  }, [trades]);
  const handlePubMonthChange = (delta) => {
    let m = pubCalMonth + delta,
      y = pubCalYear;
    if (m > 11) {
      m = 0;
      y++;
    }
    if (m < 0) {
      m = 11;
      y--;
    }
    setPubCalMonth(m);
    setPubCalYear(y);
  };
  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date();
    const day = d.getDay(); // 0=Sun, 1=Mon...6=Sat
    const diff = day === 0 ? 6 : day - 1; // days since last Monday
    d.setDate(d.getDate() - diff);
    return d.toISOString().slice(0, 10);
  });
  const [rangeEnd, setRangeEnd] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [year, setYear] = useState(() => new Date().getFullYear());

  const daily = useMemo(() => {
    const dm = {};
    trades.forEach((t) => {
      dm[t.dateStr] = (dm[t.dateStr] || 0) + t.netPnl;
    });
    return Object.entries(dm)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, pnl]) => ({ date, pnl }));
  }, [trades]);

  const lastNTradingDays = (rows, anchor, n) => {
    const upto = rows.filter((r) => r.date <= anchor);
    return upto.slice(Math.max(0, upto.length - n));
  };

  const selection = useMemo(() => {
    if (!daily.length || mode === "calendar") return [];
    if (mode === "last20") return lastNTradingDays(daily, anchorDate, 20);
    if (mode === "range") {
      const s = rangeStart <= rangeEnd ? rangeStart : rangeEnd;
      const e = rangeStart <= rangeEnd ? rangeEnd : rangeStart;
      return daily.filter((r) => r.date >= s && r.date <= e);
    }
    return daily.filter((r) => r.date.startsWith(String(year) + "-"));
  }, [daily, mode, anchorDate, rangeStart, rangeEnd, year]);

  const equity = useMemo(() => {
    let cum = 0;
    return selection.map((r) => {
      cum += r.pnl;
      return { date: r.date, cum };
    });
  }, [selection]);
  const total = useMemo(
    () => selection.reduce((s, r) => s + r.pnl, 0),
    [selection]
  );

  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (equity.length < 2 || mode === "calendar") {
      Plotly.purge(ref.current);
      return;
    }
    const lc = total >= 0 ? C.profit : C.loss;
    const fc = total >= 0 ? C.profitSoft : C.lossSoft;
    Plotly.newPlot(
      ref.current,
      [
        {
          x: equity.map((d) => d.date),
          y: equity.map((d) => d.cum),
          type: "scatter",
          mode: "lines",
          fill: "tozeroy",
          fillcolor: fc,
          line: { color: lc, width: 3, shape: "spline" },
          hovertemplate: "%{x}<br>$%{y:,.2f}<extra></extra>",
        },
      ],
      {
        paper_bgcolor: "transparent",
        plot_bgcolor: "transparent",
        font: {
          family: "'JetBrains Mono',monospace",
          color: C.textDim,
          size: 10,
        },
        margin: { l: 60, r: 20, t: 10, b: 45 },
        xaxis: {
          gridcolor: C.border,
          linecolor: C.border,
          tickfont: { size: 9 },
        },
        yaxis: {
          gridcolor: C.border,
          linecolor: C.border,
          tickprefix: "$",
          tickfont: { size: 9 },
          zerolinecolor: C.textDim,
        },
        hovermode: "x unified",
        hoverlabel: {
          bgcolor: C.surface2,
          bordercolor: C.border,
          font: { family: "'JetBrains Mono',monospace", size: 11 },
        },
      },
      { responsive: true, displayModeBar: false }
    );
    return () => {
      if (ref.current) Plotly.purge(ref.current);
    };
  }, [equity, total, mode]);

  const segBtn = (id, label) => {
    const active = mode === id;
    return (
      <button
        onClick={() => setMode(id)}
        style={{
          flex: 1,
          padding: "10px 12px",
          border: "none",
          cursor: "pointer",
          fontWeight: 900,
          fontSize: 12,
          color: active ? C.bg : C.textSec,
          background: active
            ? `linear-gradient(135deg,${C.accent},${C.purple})`
            : "transparent",
          borderRadius: 10,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 6,
          padding: 6,
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 14,
        }}
      >
        {segBtn("last20", "Last 20 Days P/L")}
        {segBtn("range", "Date Range")}
        {segBtn("year", "Yearly Total Profit")}
        {segBtn("calendar", "Calendar")}
      </div>

      {mode === "calendar" ? (
        <div
          style={{
            marginTop: 14,
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: 14,
          }}
        >
          <CalendarHeatmap
            trades={trades}
            year={pubCalYear}
            month={pubCalMonth}
            onMonthChange={handlePubMonthChange}
          />
        </div>
      ) : (
        <>
          <div
            style={{
              marginTop: 14,
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "end",
            }}
          >
            {mode === "last20" && (
              <div style={{ minWidth: 220 }}>
                <div
                  style={{
                    fontSize: 10,
                    color: C.textDim,
                    fontWeight: 800,
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  Pick a date
                </div>
                <input
                  type="date"
                  value={anchorDate}
                  onChange={(e) => setAnchorDate(e.target.value)}
                  style={{
                    ...inputSt,
                    padding: "10px 12px",
                    colorScheme: "dark",
                    cursor: "pointer",
                  }}
                />
                <div style={{ marginTop: 8, fontSize: 11, color: C.textSec }}>
                  Shows last <b>20 trading days</b> from date selected
                </div>
              </div>
            )}
            {mode === "range" && (
              <>
                <div style={{ minWidth: 220 }}>
                  <div
                    style={{
                      fontSize: 10,
                      color: C.textDim,
                      fontWeight: 800,
                      letterSpacing: 1.2,
                      textTransform: "uppercase",
                      marginBottom: 6,
                    }}
                  >
                    Start
                  </div>
                  <input
                    type="date"
                    value={rangeStart}
                    onChange={(e) => setRangeStart(e.target.value)}
                    style={{
                      ...inputSt,
                      padding: "10px 12px",
                      colorScheme: "dark",
                      cursor: "pointer",
                    }}
                  />
                </div>
                <div style={{ minWidth: 220 }}>
                  <div
                    style={{
                      fontSize: 10,
                      color: C.textDim,
                      fontWeight: 800,
                      letterSpacing: 1.2,
                      textTransform: "uppercase",
                      marginBottom: 6,
                    }}
                  >
                    End
                  </div>
                  <input
                    type="date"
                    value={rangeEnd}
                    onChange={(e) => setRangeEnd(e.target.value)}
                    style={{
                      ...inputSt,
                      padding: "10px 12px",
                      colorScheme: "dark",
                      cursor: "pointer",
                    }}
                  />
                </div>
              </>
            )}
            {mode === "year" && (
              <div style={{ minWidth: 180 }}>
                <div
                  style={{
                    fontSize: 10,
                    color: C.textDim,
                    fontWeight: 800,
                    letterSpacing: 1.2,
                    textTransform: "uppercase",
                    marginBottom: 6,
                  }}
                >
                  Year
                </div>
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value || "0", 10))}
                  style={{ ...inputSt, padding: "10px 12px" }}
                />
              </div>
            )}
          </div>
          <div style={{ marginTop: 18, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: C.textSec, fontWeight: 700 }}>
              Current {mode === "year" ? "Year" : "Date Range"} Day-to-day
              Accumulated Profit
            </div>
            <div
              style={{
                marginTop: 10,
                fontSize: 32,
                fontWeight: 1000,
                color: plC(total),
                fontFamily: "var(--mono)",
              }}
            >
              {fmt(total)}
            </div>
          </div>
          <div
            style={{
              marginTop: 14,
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              padding: 10,
            }}
          >
            {equity.length < 2 ? (
              <div
                style={{ color: C.textSec, textAlign: "center", padding: 30 }}
              >
                Need 2+ trading days in selection
              </div>
            ) : (
              <div ref={ref} style={{ width: "100%", height: 320 }} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Risk Dashboard v2 with Approve/Reject System ─────────────
// REPLACE the previous RiskPasswordGate and RiskDashboard functions
// in your App.js with these two functions.
// Then make sure you have the route:
//   <Route path="/risk" element={<RiskPasswordGate><RiskDashboard /></RiskPasswordGate>} />

function RiskPasswordGate({ children }) {
  const KEY = "millium-risk-unlocked";
  const [unlocked, setUnlocked] = useState(false);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = () => {
    if (pw === APP_PASSWORD) {
      setUnlocked(true);
      setErr("");
    } else setErr("Wrong password");
  };
  if (unlocked) return children;
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: C.bg,
        color: C.text,
        padding: 20,
        fontFamily: "system-ui,sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          padding: 18,
          background: C.surface,
        }}
      >
        <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 4 }}>
          Risk Dashboard
        </div>
        <div style={{ fontSize: 12, color: C.textSec, marginBottom: 14 }}>
          Authorized access only
        </div>
        <input
          type="password"
          placeholder="Password"
          value={pw}
          onChange={(e) => {
            setPw(e.target.value);
            setErr("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          style={{
            width: "100%",
            padding: "10px 12px",
            borderRadius: 10,
            border: `1px solid ${C.borderLight}`,
            background: C.bg,
            color: C.text,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {err && (
          <div style={{ marginTop: 10, color: C.loss, fontWeight: 700 }}>
            {err}
          </div>
        )}
        <button
          onClick={submit}
          style={{
            marginTop: 12,
            width: "100%",
            border: "none",
            borderRadius: 10,
            padding: "10px 12px",
            fontWeight: 900,
            cursor: "pointer",
            background: `linear-gradient(135deg,${C.accent},${C.purple})`,
            color: "#fff",
          }}
        >
          Access Dashboard
        </button>
      </div>
    </div>
  );
}

function RiskDashboard() {
  const [summary, setSummary] = useState(null);
  const [trades, setTrades] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewAction, setReviewAction] = useState(null); // null | "approve" | "reject"
  const [submitting, setSubmitting] = useState(false);

  const [RULES, setRULES] = useState({
    maxDailyLoss: -3000,
    maxTradesPerDay: 20,
  });
  const [showSettings, setShowSettings] = useState(false);
  const [settingsPw, setSettingsPw] = useState("");
  const [settingsUnlocked, setSettingsUnlocked] = useState(false);
  const [settingsErr, setSettingsErr] = useState("");
  const [savingRules, setSavingRules] = useState(false);
  const [editRules, setEditRules] = useState(null);

  const loadData = async () => {
    try {
      const { data: sumData } = await supabase
        .from("public_journal_summary")
        .select("*")
        .eq("id", "main")
        .single();
      if (sumData) setSummary(sumData);

      const { data: trData } = await supabase
        .from("trades")
        .select("payload")
        .eq("id", 1)
        .maybeSingle();
      if (trData?.payload) {
        setTrades(
          trData.payload.map((t) => ({
            ...t,
            date: new Date(t.date),
            entryTime: new Date(t.entryTime),
            exitTime: new Date(t.exitTime),
          }))
        );
      }

      const { data: revData } = await supabase
        .from("risk_reviews")
        .select("*")
        .order("trade_date", { ascending: false });
      if (revData) setReviews(revData);
    } catch (e) {
      console.error(e);
    }
    const { data: rulesData } = await supabase
      .from("risk_rules")
      .select("*")
      .eq("id", "main")
      .maybeSingle();
    if (rulesData) {
      setRULES({
        maxDailyLoss: rulesData.max_daily_loss,
        maxTradesPerDay: rulesData.max_trades_per_day ?? 20,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  // ── Compute daily metrics for all trading days ──
  const dailyMetrics = useMemo(() => {
    if (!trades.length) return {};
    const dm = {};
    trades.forEach((t) => {
      const d = t.dateStr;
      if (!dm[d])
        dm[d] = {
          pnl: 0,
          trades: 0,
          symbols: new Set(),
        };
      dm[d].pnl += t.netPnl || 0;
      dm[d].trades += 1;
      dm[d].symbols.add(t.symbol);
    });
    // Convert sets to arrays
    Object.keys(dm).forEach((d) => {
      dm[d].symbols = Array.from(dm[d].symbols);
    });
    return dm;
  }, [trades, RULES]);

  // ── Overall metrics (same as v1) ──
  const metrics = useMemo(() => {
    if (!trades.length) return null;
    const sortedDays = Object.entries(dailyMetrics).sort(([a], [b]) =>
      a.localeCompare(b)
    );
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const toDS = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const localNow = new Date(
      now.toLocaleString("en-US", { timeZone: "America/Chicago" })
    );
    const todayStr = toDS(localNow);
    const todayData = dailyMetrics[todayStr] || {
      pnl: 0,
      trades: 0,
    };
    const dow = localNow.getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(localNow);
    monday.setDate(localNow.getDate() + mondayOffset);
    const mondayStr = toDS(monday);
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    const fridayStr = toDS(friday);
    const weekDays = sortedDays.filter(
      ([d]) => d >= mondayStr && d <= fridayStr
    );
    const weekPnl = weekDays.reduce((s, [, v]) => s + v.pnl, 0);
    const weekTrades = weekDays.reduce((s, [, v]) => s + v.trades, 0);

    const monthPrefix = todayStr.slice(0, 7);
    const yearPrefix = todayStr.slice(0, 4);
    const mtd = sortedDays
      .filter(([d]) => d.startsWith(monthPrefix))
      .reduce((s, [, v]) => s + v.pnl, 0);
    const ytd = sortedDays
      .filter(([d]) => d.startsWith(yearPrefix))
      .reduce((s, [, v]) => s + v.pnl, 0);

    let consecLosing = 0;
    for (let i = sortedDays.length - 1; i >= 0; i--) {
      if (sortedDays[i][1].pnl < 0) consecLosing++;
      else break;
    }

    let peak = 0,
      maxDD = 0,
      cum = 0,
      currentDD = 0;
    sortedDays.forEach(([, v]) => {
      cum += v.pnl;
      if (cum > peak) peak = cum;
      const dd = peak - cum;
      if (dd > maxDD) maxDD = dd;
      currentDD = dd;
    });

    return {
      todayPnl: todayData.pnl,
      todayTrades: todayData.trades,
      weekPnl,
      weekTrades,
      mtd,
      ytd,
      consecLosing,
      currentDD,
      maxDD,
      peak,
    };
  }, [trades, dailyMetrics]);

  // ── Review helpers ──
  const reviewMap = useMemo(() => {
    const m = {};
    reviews.forEach((r) => {
      m[r.trade_date] = r;
    });
    return m;
  }, [reviews]);

  const pendingDays = useMemo(() => {
    return Object.keys(dailyMetrics)
      .filter((d) => !reviewMap[d])
      .sort((a, b) => b.localeCompare(a));
  }, [dailyMetrics, reviewMap]);

  const approvedCount = reviews.filter((r) => r.status === "approved").length;
  const rejectedCount = reviews.filter((r) => r.status === "rejected").length;
  const pendingCount = pendingDays.length;

  // Auto-create pending reviews for days that don't have one
  // (We don't actually write to DB until review — pending is just "no row exists")

  const getBreach = (d, data) => {
    const b = [];
    if (data.pnl < RULES.maxDailyLoss) b.push("Daily loss limit");
    if (data.trades > RULES.maxTradesPerDay) b.push("Max trades/day");
    return b;
  };

  const handleSubmitReview = async (date, status) => {
    if (status === "rejected" && !reviewNotes.trim()) return;
    setSubmitting(true);
    const data = dailyMetrics[date] || {};
    const metricsSnapshot = {
      pnl: data.pnl || 0,
      trades: data.trades || 0,
      maxQty: data.maxQty || 0,
      symbols: data.symbols || [],
      breaches: getBreach(date, data),
    };

    const { error } = await supabase.from("risk_reviews").upsert(
      {
        trade_date: date,
        status,
        reviewed_by: "Risk Manager",
        reviewed_at: new Date().toISOString(),
        notes: reviewNotes.trim() || null,
        metrics: metricsSnapshot,
      },
      { onConflict: "trade_date" }
    );

    if (error) {
      console.error("Review save error:", error);
    } else {
      setSelectedDate(null);
      setReviewNotes("");
      setReviewAction(null);
      await loadData();
    }
    setSubmitting(false);
  };

  // ── Status helpers ──
  const getStatus = (current, limit, isNegative = true) => {
    if (isNegative) {
      if (current < limit) return "red";
      return "green";
    } else {
      if (current > limit) return "red";
      return "green";
    }
  };

  const statusColors = {
    green: {
      bg: "rgba(0,229,176,0.12)",
      border: "rgba(0,229,176,0.3)",
      dot: C.profit,
      text: C.profit,
    },
    red: {
      bg: "rgba(255,77,106,0.12)",
      border: "rgba(255,77,106,0.3)",
      dot: C.loss,
      text: C.loss,
    },
  };
  const statusLabel = { green: "OK", red: "BREACH" };

  const TrafficLight = ({ status }) => {
    const sc = statusColors[status];
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: sc.dot,
            boxShadow: `0 0 8px ${sc.dot}`,
          }}
        />
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: sc.text,
            letterSpacing: 1,
            textTransform: "uppercase",
          }}
        >
          {statusLabel[status]}
        </span>
      </div>
    );
  };

  const RuleCard = ({ title, status, valueLabel, limitLabel }) => {
    const sc = statusColors[status];
    return (
      <div
        style={{
          background: sc.bg,
          border: `1px solid ${sc.border}`,
          borderRadius: 12,
          padding: 16,
          flex: "1 1 130px",
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 6,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 9,
                color: C.textDim,
                textTransform: "uppercase",
                letterSpacing: 1.3,
                fontWeight: 700,
                marginBottom: 4,
              }}
            >
              {title}
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 900,
                color: sc.text,
                fontFamily: "var(--mono)",
              }}
            >
              {valueLabel}
            </div>
          </div>
          <TrafficLight status={status} />
        </div>
        <div
          style={{ fontSize: 10, color: C.textSec, fontFamily: "var(--mono)" }}
        >
          Limit: {limitLabel}
        </div>
      </div>
    );
  };

  // Review status pill
  const StatusPill = ({ status }) => {
    const config = {
      pending: {
        bg: "rgba(255,176,32,0.15)",
        color: C.yellow,
        label: "⏳ Pending",
      },
      approved: {
        bg: "rgba(0,229,176,0.15)",
        color: C.profit,
        label: "✓ Approved",
      },
      rejected: {
        bg: "rgba(255,77,106,0.15)",
        color: C.loss,
        label: "✗ Rejected",
      },
    };
    const c = config[status] || config.pending;
    return (
      <span
        style={{
          padding: "3px 10px",
          borderRadius: 6,
          fontSize: 10,
          fontWeight: 800,
          background: c.bg,
          color: c.color,
        }}
      >
        {c.label}
      </span>
    );
  };

  const formatDate = (d) => {
    const dt = new Date(d + "T12:00:00");
    return dt.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: C.bg,
          color: C.textSec,
        }}
      >
        Loading risk data...
      </div>
    );
  if (!metrics)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          background: C.bg,
          color: C.textSec,
        }}
      >
        No trade data available
      </div>
    );

  const m = metrics;
  const dailyLossStatus = getStatus(m.todayPnl, RULES.maxDailyLoss, true);
  const tradesStatus = getStatus(m.todayTrades, RULES.maxTradesPerDay, false);
  const allStatuses = [
    dailyLossStatus,
    tradesStatus,
  ];
  const overallStatus = allStatuses.includes("red") ? "red" : "green";
  const overallSc = statusColors[overallStatus];

  // Selected day detail
  const selData = selectedDate ? dailyMetrics[selectedDate] : null;
  const selReview = selectedDate ? reviewMap[selectedDate] : null;
  const selBreaches = selData ? getBreach(selectedDate, selData) : [];

  return (
    <div
      style={{
        "--mono": "'JetBrains Mono',monospace",
        "--sans": "'DM Sans',system-ui,sans-serif",
        minHeight: "100vh",
        background: C.bg,
        color: C.text,
        fontFamily: "var(--sans)",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      <div
        style={{ maxWidth: 880, margin: "0 auto", padding: "20px 14px 60px" }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 900,
                color: C.text,
                letterSpacing: 1,
              }}
            >
              MILLIUM
            </div>
            <div
              style={{
                fontSize: 10,
                color: C.textDim,
                letterSpacing: 3,
                textTransform: "uppercase",
                marginTop: 2,
              }}
            >
              Risk Management Dashboard
            </div>
          </div>
          <div
            style={{
              padding: "8px 16px",
              borderRadius: 10,
              background: overallSc.bg,
              border: `1px solid ${overallSc.border}`,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: overallSc.dot,
                boxShadow: `0 0 12px ${overallSc.dot}`,
              }}
            />
            <span
              style={{
                fontSize: 12,
                fontWeight: 900,
                color: overallSc.text,
                letterSpacing: 1,
              }}
            >
              {overallStatus === "green" ? "ALL CLEAR" : "ALERT"}
            </span>
          </div>
        </div>

        {/* Review Summary Bar */}
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              flex: "1 1 120px",
              background: "rgba(255,176,32,0.1)",
              border: "1px solid rgba(255,176,32,0.25)",
              borderRadius: 10,
              padding: "10px 14px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 24,
                fontWeight: 900,
                color: C.yellow,
                fontFamily: "var(--mono)",
              }}
            >
              {pendingCount}
            </div>
            <div
              style={{
                fontSize: 9,
                color: C.textDim,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                fontWeight: 700,
                marginTop: 2,
              }}
            >
              Pending Review
            </div>
          </div>
          <div
            style={{
              flex: "1 1 120px",
              background: C.profitSoft,
              border: "1px solid rgba(0,229,176,0.25)",
              borderRadius: 10,
              padding: "10px 14px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 24,
                fontWeight: 900,
                color: C.profit,
                fontFamily: "var(--mono)",
              }}
            >
              {approvedCount}
            </div>
            <div
              style={{
                fontSize: 9,
                color: C.textDim,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                fontWeight: 700,
                marginTop: 2,
              }}
            >
              Approved
            </div>
          </div>
          <div
            style={{
              flex: "1 1 120px",
              background: C.lossSoft,
              border: "1px solid rgba(255,77,106,0.25)",
              borderRadius: 10,
              padding: "10px 14px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: 24,
                fontWeight: 900,
                color: C.loss,
                fontFamily: "var(--mono)",
              }}
            >
              {rejectedCount}
            </div>
            <div
              style={{
                fontSize: 9,
                color: C.textDim,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                fontWeight: 700,
                marginTop: 2,
              }}
            >
              Rejected
            </div>
          </div>
        </div>

        {/* Overview Cards */}
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          {[
            {
              label: "Today",
              value: fmt(m.todayPnl),
              sub: `${m.todayTrades} trades`,
              color: plC(m.todayPnl),
            },
            {
              label: "This Week",
              value: fmtS(m.weekPnl),
              sub: `${m.weekTrades} trades`,
              color: plC(m.weekPnl),
            },
            { label: "MTD", value: fmtS(m.mtd), color: plC(m.mtd) },
            { label: "YTD", value: fmtS(m.ytd), color: plC(m.ytd) },
          ].map((c, i) => (
            <div
              key={i}
              style={{
                flex: "1 1 130px",
                background: C.surface,
                borderRadius: 10,
                padding: "12px 14px",
                border: `1px solid ${C.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 9,
                  color: C.textDim,
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                {c.label}
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 900,
                  color: c.color,
                  fontFamily: "var(--mono)",
                }}
              >
                {c.value}
              </div>
              {c.sub && (
                <div
                  style={{
                    fontSize: 10,
                    color: C.textSec,
                    fontFamily: "var(--mono)",
                    marginTop: 2,
                  }}
                >
                  {c.sub}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Risk Limits */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: C.textDim,
            textTransform: "uppercase",
            letterSpacing: 1.2,
            marginBottom: 12,
          }}
        >
          Risk Limits
        </div>
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 20,
          }}
        >
          <RuleCard
            title="Daily Loss"
            status={dailyLossStatus}
            valueLabel={fmt(m.todayPnl)}
            limitLabel={fmt(RULES.maxDailyLoss)}
          />
          <RuleCard
            title="Trades Today"
            status={tradesStatus}
            valueLabel={`${m.todayTrades} / ${RULES.maxTradesPerDay}`}
            limitLabel={`${RULES.maxTradesPerDay} max`}
          />
        </div>

        {/* Settings Button */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 16,
          }}
        >
          <button
            onClick={() => {
              setShowSettings(!showSettings);
              setEditRules({ ...RULES });
              setSettingsPw("");
              setSettingsUnlocked(false);
              setSettingsErr("");
            }}
            style={{
              background: "transparent",
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              color: C.textSec,
              padding: "7px 14px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Sv s={14}>
              <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
              <circle cx="12" cy="12" r="3" />
            </Sv>
            {showSettings ? "Close Settings" : "Configure Risk Limits"}
          </button>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div
            style={{
              background: C.surface,
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              padding: 20,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 900,
                color: C.text,
                marginBottom: 4,
              }}
            >
              Risk Limit Settings
            </div>
            <div style={{ fontSize: 11, color: C.textSec, marginBottom: 16 }}>
              Changes require password and take effect immediately.
            </div>

            {!settingsUnlocked ? (
              <div style={{ maxWidth: 320 }}>
                <input
                  type="password"
                  placeholder="Enter password to edit"
                  value={settingsPw}
                  onChange={(e) => {
                    setSettingsPw(e.target.value);
                    setSettingsErr("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (settingsPw === APP_PASSWORD) {
                        setSettingsUnlocked(true);
                        setSettingsErr("");
                      } else setSettingsErr("Wrong password");
                    }
                  }}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    boxSizing: "border-box",
                    border: `1px solid ${settingsErr ? C.loss : C.borderLight}`,
                    background: C.bg,
                    color: C.text,
                    outline: "none",
                    marginBottom: 8,
                  }}
                />
                {settingsErr && (
                  <div
                    style={{
                      fontSize: 11,
                      color: C.loss,
                      fontWeight: 700,
                      marginBottom: 8,
                    }}
                  >
                    {settingsErr}
                  </div>
                )}
                <button
                  onClick={() => {
                    if (settingsPw === APP_PASSWORD) {
                      setSettingsUnlocked(true);
                      setSettingsErr("");
                    } else setSettingsErr("Wrong password");
                  }}
                  style={{
                    background: `linear-gradient(135deg,${C.accent},${C.purple})`,
                    border: "none",
                    borderRadius: 8,
                    color: "#fff",
                    padding: "9px 18px",
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  Unlock Settings
                </button>
              </div>
            ) : (
              editRules && (
                <div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 12,
                      marginBottom: 16,
                    }}
                  >
                    {/* Max Daily Loss */}
                    <div
                      style={{
                        background: C.bg,
                        borderRadius: 8,
                        padding: "10px 14px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: C.textDim,
                          textTransform: "uppercase",
                          letterSpacing: 1.2,
                          fontWeight: 700,
                          marginBottom: 6,
                        }}
                      >
                        Max Daily Loss ($)
                      </div>
                      <input
                        type="number"
                        value={Math.abs(editRules.maxDailyLoss)}
                        onChange={(e) =>
                          setEditRules({
                            ...editRules,
                            maxDailyLoss: -Math.abs(Number(e.target.value)),
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          borderRadius: 6,
                          border: `1px solid ${C.borderLight}`,
                          background: C.surface,
                          color: C.text,
                          outline: "none",
                          boxSizing: "border-box",
                          fontFamily: "var(--mono)",
                          fontSize: 14,
                          fontWeight: 700,
                        }}
                      />
                    </div>

                    {/* Max Trades Per Day */}
                    <div
                      style={{
                        background: C.bg,
                        borderRadius: 8,
                        padding: "10px 14px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 9,
                          color: C.textDim,
                          textTransform: "uppercase",
                          letterSpacing: 1.2,
                          fontWeight: 700,
                          marginBottom: 6,
                        }}
                      >
                        Max Trades / Day
                      </div>
                      <input
                        type="number"
                        value={editRules.maxTradesPerDay}
                        onChange={(e) =>
                          setEditRules({
                            ...editRules,
                            maxTradesPerDay: Number(e.target.value),
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          borderRadius: 6,
                          border: `1px solid ${C.borderLight}`,
                          background: C.surface,
                          color: C.text,
                          outline: "none",
                          boxSizing: "border-box",
                          fontFamily: "var(--mono)",
                          fontSize: 14,
                          fontWeight: 700,
                        }}
                      />
                    </div>

                  </div>

                  {/* Save / Cancel */}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={async () => {
                        setSavingRules(true);
                        const { error } = await supabase
                          .from("risk_rules")
                          .upsert(
                            {
                              id: "main",
                              max_daily_loss: editRules.maxDailyLoss,
                              max_trades_per_day: editRules.maxTradesPerDay,
                              updated_at: new Date().toISOString(),
                              updated_by: "Risk Manager",
                            },
                            { onConflict: "id" }
                          );
                        if (!error) {
                          setRULES(editRules);
                          setShowSettings(false);
                          setSettingsUnlocked(false);
                        } else {
                          console.error("Save rules error:", error);
                        }
                        setSavingRules(false);
                      }}
                      disabled={savingRules}
                      style={{
                        background: `linear-gradient(135deg, ${C.profit}, #00B894)`,
                        border: "none",
                        borderRadius: 8,
                        color: "#fff",
                        padding: "10px 24px",
                        fontSize: 12,
                        fontWeight: 800,
                        cursor: "pointer",
                        opacity: savingRules ? 0.5 : 1,
                      }}
                    >
                      {savingRules ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      onClick={() => {
                        setShowSettings(false);
                        setSettingsUnlocked(false);
                      }}
                      style={{
                        background: "transparent",
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        color: C.textSec,
                        padding: "10px 24px",
                        fontSize: 12,
                        fontWeight: 800,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        )}

        {/* Drawdown */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: C.textDim,
            textTransform: "uppercase",
            letterSpacing: 1.2,
            marginBottom: 12,
          }}
        >
          Drawdown Monitor
        </div>
        <div
          style={{
            background: C.surface,
            borderRadius: 12,
            padding: 16,
            border: `1px solid ${C.border}`,
            marginBottom: 20,
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1 1 150px" }}>
            <div
              style={{
                fontSize: 9,
                color: C.textDim,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                fontWeight: 700,
                marginBottom: 4,
              }}
            >
              Current Drawdown
            </div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 900,
                color: m.currentDD > 0 ? C.loss : C.profit,
                fontFamily: "var(--mono)",
              }}
            >
              {m.currentDD > 0
                ? `-$${m.currentDD
                    .toFixed(0)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
                : "At Peak"}
            </div>
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <div
              style={{
                fontSize: 9,
                color: C.textDim,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                fontWeight: 700,
                marginBottom: 4,
              }}
            >
              Max Drawdown
            </div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 900,
                color: C.loss,
                fontFamily: "var(--mono)",
              }}
            >
              -${m.maxDD.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </div>
          </div>
          <div style={{ flex: "1 1 150px" }}>
            <div
              style={{
                fontSize: 9,
                color: C.textDim,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                fontWeight: 700,
                marginBottom: 4,
              }}
            >
              Equity Peak
            </div>
            <div
              style={{
                fontSize: 24,
                fontWeight: 900,
                color: C.profit,
                fontFamily: "var(--mono)",
              }}
            >
              +${m.peak.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            </div>
          </div>
        </div>

        {/* ═══ Daily Review Section ═══ */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: C.textDim,
            textTransform: "uppercase",
            letterSpacing: 1.2,
            marginBottom: 12,
          }}
        >
          Daily Risk Reviews
        </div>

        <div
          style={{
            display: "flex",
            gap: 14,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          {/* Day list */}
          <div
            style={{
              flex: "1 1 300px",
              background: C.surface,
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              overflow: "hidden",
              maxHeight: 500,
              overflowY: "auto",
            }}
          >
            {/* Pending days first */}
            {pendingDays.length > 0 && (
              <div
                style={{
                  padding: "10px 14px 6px",
                  fontSize: 9,
                  color: C.yellow,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                ⏳ Pending Review ({pendingDays.length})
              </div>
            )}
            {pendingDays.map((d) => {
              const data = dailyMetrics[d];
              const breaches = getBreach(d, data);
              const isSelected = selectedDate === d;
              return (
                <div
                  key={d}
                  onClick={() => {
                    setSelectedDate(d);
                    setReviewNotes("");
                    setReviewAction(null);
                  }}
                  style={{
                    padding: "10px 14px",
                    borderBottom: `1px solid ${C.border}`,
                    cursor: "pointer",
                    background: isSelected ? C.accentSoft : "transparent",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = C.surfaceHover;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div
                        style={{ fontSize: 12, fontWeight: 700, color: C.text }}
                      >
                        {formatDate(d)}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: C.textSec,
                          fontFamily: "var(--mono)",
                          marginTop: 2,
                        }}
                      >
                        {data.trades}t · {data.symbols?.join(", ")}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: plC(data.pnl),
                          fontFamily: "var(--mono)",
                        }}
                      >
                        {fmtS(data.pnl)}
                      </div>
                      {breaches.length > 0 && <StatusPill status="rejected" />}
                      {breaches.length === 0 && <StatusPill status="pending" />}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Reviewed days */}
            {reviews.length > 0 && (
              <div
                style={{
                  padding: "10px 14px 6px",
                  fontSize: 9,
                  color: C.textSec,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                Reviewed ({reviews.length})
              </div>
            )}
            {reviews.map((r) => {
              const data = dailyMetrics[r.trade_date] || {
                pnl: 0,
                trades: 0,
                symbols: [],
              };
              const isSelected = selectedDate === r.trade_date;
              return (
                <div
                  key={r.trade_date}
                  onClick={() => {
                    setSelectedDate(r.trade_date);
                    setReviewNotes("");
                    setReviewAction(null);
                  }}
                  style={{
                    padding: "10px 14px",
                    borderBottom: `1px solid ${C.border}`,
                    cursor: "pointer",
                    background: isSelected ? C.accentSoft : "transparent",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = C.surfaceHover;
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div
                        style={{ fontSize: 12, fontWeight: 700, color: C.text }}
                      >
                        {formatDate(r.trade_date)}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: C.textSec,
                          fontFamily: "var(--mono)",
                          marginTop: 2,
                        }}
                      >
                        {data.trades}t · {data.symbols?.join?.(", ") || ""}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 800,
                          color: plC(data.pnl),
                          fontFamily: "var(--mono)",
                        }}
                      >
                        {fmtS(data.pnl)}
                      </div>
                      <StatusPill status={r.status} />
                    </div>
                  </div>
                </div>
              );
            })}

            {pendingDays.length === 0 && reviews.length === 0 && (
              <div
                style={{ padding: 30, textAlign: "center", color: C.textSec }}
              >
                No trading days to review
              </div>
            )}
          </div>

          {/* Detail panel */}
          <div
            style={{
              flex: "1 1 340px",
              background: C.surface,
              borderRadius: 12,
              border: `1px solid ${C.border}`,
              padding: 16,
              minHeight: 300,
            }}
          >
            {!selectedDate ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  minHeight: 250,
                  color: C.textDim,
                  fontSize: 13,
                }}
              >
                ← Select a day to review
              </div>
            ) : (
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div
                      style={{ fontSize: 16, fontWeight: 900, color: C.text }}
                    >
                      {formatDate(selectedDate)}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: C.textDim,
                        fontFamily: "var(--mono)",
                      }}
                    >
                      {selectedDate}
                    </div>
                  </div>
                  {selReview ? (
                    <StatusPill status={selReview.status} />
                  ) : (
                    <StatusPill status="pending" />
                  )}
                </div>

                {/* Day metrics as RuleCards */}
                {selData && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    {(() => {
                      const dlStatus =
                        selData.pnl < RULES.maxDailyLoss ? "red" : "green";
                      const trStatus =
                        selData.trades > RULES.maxTradesPerDay
                          ? "red"
                          : "green";
                      return [
                        {
                          title: "Daily Loss",
                          status: dlStatus,
                          value: fmt(selData.pnl),
                          limit: fmt(RULES.maxDailyLoss),
                        },
                        {
                          title: "Trades Today",
                          status: trStatus,
                          value: `${selData.trades} / ${RULES.maxTradesPerDay}`,
                          limit: `${RULES.maxTradesPerDay} max`,
                        },
                      ].map((c, i) => {
                        const sc = statusColors[c.status];
                        return (
                          <div
                            key={i}
                            style={{
                              background: sc.bg,
                              border: `1px solid ${sc.border}`,
                              borderRadius: 10,
                              padding: "10px 12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: 4,
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 8,
                                  color: C.textDim,
                                  textTransform: "uppercase",
                                  letterSpacing: 1,
                                  fontWeight: 700,
                                }}
                              >
                                {c.title}
                              </div>
                              <div
                                style={{
                                  fontSize: 8,
                                  fontWeight: 800,
                                  color: sc.text,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <div
                                  style={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: "50%",
                                    background: sc.dot,
                                  }}
                                />
                                {statusLabel[c.status]}
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 16,
                                fontWeight: 900,
                                color: sc.text,
                                fontFamily: "var(--mono)",
                              }}
                            >
                              {c.value}
                            </div>
                            <div
                              style={{
                                fontSize: 9,
                                color: C.textSec,
                                fontFamily: "var(--mono)",
                                marginTop: 2,
                              }}
                            >
                              Limit: {c.limit}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {/* Breaches */}
                {selBreaches.length > 0 && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: "10px 12px",
                      background: C.lossSoft,
                      borderRadius: 8,
                      border: `1px solid rgba(255,77,106,0.2)`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 9,
                        color: C.loss,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: 1.2,
                        marginBottom: 6,
                      }}
                    >
                      ⚠ Rule Breaches
                    </div>
                    {selBreaches.map((b, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: 11,
                          color: C.loss,
                          fontWeight: 600,
                          padding: "2px 0",
                        }}
                      >
                        • {b}
                      </div>
                    ))}
                  </div>
                )}

                {/* Previous review info */}
                {selReview && (
                  <div
                    style={{
                      marginBottom: 16,
                      padding: "10px 12px",
                      background: C.bg,
                      borderRadius: 8,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 9,
                        color: C.textDim,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: 1.2,
                        marginBottom: 6,
                      }}
                    >
                      Review Details
                    </div>
                    <div style={{ fontSize: 11, color: C.textSec }}>
                      <span style={{ fontWeight: 700 }}>
                        {selReview.reviewed_by}
                      </span>{" "}
                      · {new Date(selReview.reviewed_at).toLocaleString()}
                    </div>
                    {selReview.notes && (
                      <div
                        style={{
                          fontSize: 11,
                          color: C.text,
                          marginTop: 6,
                          padding: "8px 10px",
                          background: C.surface2,
                          borderRadius: 6,
                          fontStyle: "italic",
                        }}
                      >
                        "{selReview.notes}"
                      </div>
                    )}
                  </div>
                )}

                {/* Approve/Reject buttons (only for pending) */}
                {!selReview && (
                  <div>
                    <div
                      style={{
                        fontSize: 9,
                        color: C.textDim,
                        fontWeight: 800,
                        textTransform: "uppercase",
                        letterSpacing: 1.2,
                        marginBottom: 8,
                      }}
                    >
                      Review Action
                    </div>

                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Notes (required for rejection, optional for approval)..."
                      rows={3}
                      style={{
                        width: "100%",
                        background: C.bg,
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        color: C.text,
                        padding: "10px 12px",
                        fontSize: 12,
                        fontFamily: "var(--sans)",
                        outline: "none",
                        boxSizing: "border-box",
                        resize: "vertical",
                        marginBottom: 10,
                      }}
                    />

                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() =>
                          handleSubmitReview(selectedDate, "approved")
                        }
                        disabled={submitting}
                        style={{
                          flex: 1,
                          padding: "11px 16px",
                          border: "none",
                          borderRadius: 10,
                          cursor: "pointer",
                          fontWeight: 900,
                          fontSize: 13,
                          color: "#fff",
                          background: `linear-gradient(135deg, ${C.profit}, #00B894)`,
                          opacity: submitting ? 0.5 : 1,
                        }}
                      >
                        ✓ Approve
                      </button>
                      <button
                        onClick={() => {
                          if (!reviewNotes.trim()) {
                            setReviewAction("reject");
                            return;
                          }
                          handleSubmitReview(selectedDate, "rejected");
                        }}
                        disabled={submitting}
                        style={{
                          flex: 1,
                          padding: "11px 16px",
                          border: "none",
                          borderRadius: 10,
                          cursor: "pointer",
                          fontWeight: 900,
                          fontSize: 13,
                          color: "#fff",
                          background: `linear-gradient(135deg, ${C.loss}, #E63946)`,
                          opacity: submitting ? 0.5 : 1,
                        }}
                      >
                        ✗ Reject
                      </button>
                    </div>

                    {reviewAction === "reject" && !reviewNotes.trim() && (
                      <div
                        style={{
                          marginTop: 8,
                          fontSize: 11,
                          color: C.yellow,
                          fontWeight: 700,
                        }}
                      >
                        ⚠ Please add notes explaining the rejection reason
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            textAlign: "center",
            padding: "20px 0",
            marginTop: 20,
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <div style={{ fontSize: 10, color: C.textDim }}>
            Millium Risk Management · Data updates on each trade import
          </div>
          <div style={{ fontSize: 9, color: C.textDim, marginTop: 4 }}>
            Last data: {summary?.to_date || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}
function Footer() {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "24px 14px",
        borderTop: `1px solid ${C.border}`,
        marginTop: 14,
      }}
    >
      <div
        style={{
          fontSize: 13,
          color: C.textSec,
          fontStyle: "italic",
          marginBottom: 8,
        }}
      >
        "Base hits win ball games"
      </div>
      <div style={{ fontSize: 10, color: C.textDim }}>
        © {new Date().getFullYear()} Millium. All rights reserved.
      </div>
    </div>
  );
}
/* ── Main App ─────────────────────────────────────────────────── */
export default function App() {
  const [trades, setTrades] = useState([]);
  const [tab, setTab] = useState("calendar");
  const [showImport, setShowImport] = useState(false);
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [ready, setReady] = useState(false);
  const [publicSummary, setPublicSummary] = useState(null);
  const [sideFilter, setSideFilter] = useState("All");
  const [symbolFilter, setSymbolFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delErr, setDelErr] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);

  const fetchPublicSummary = async () => {
    const { data, error } = await supabase
      .from("public_journal_summary")
      .select("*")
      .eq("id", "main")
      .single();
    if (error) {
      console.error("fetchPublicSummary error:", error);
      return;
    }
    setPublicSummary(data);
  };
  useEffect(() => {
    fetchPublicSummary();
  }, []);

  const tradesForPublic = useMemo(() => {
    if (!publicSummary?.daily) return [];
    return publicSummary.daily.map((d, i) => ({
      id: i,
      dateStr: d.date,
      netPnl: Number(d.pnl) || 0,
    }));
  }, [publicSummary]);

  // Load trades from Supabase
  useEffect(() => {
    (async () => {
      try {
        const p = await loadTradesFromSupabase();
        const parsed = (p || []).map((t) => ({
          ...t,
          date: new Date(t.date),
          entryTime: new Date(t.entryTime),
          exitTime: new Date(t.exitTime),
        }));
        setTrades(parsed);
        if (parsed.length) {
          const l = parsed.reduce((a, b) => (a.date > b.date ? a : b));
          setCalMonth(l.date.getMonth());
          setCalYear(l.date.getFullYear());
        }
      } catch (e) {
        console.error(e);
      }
      setReady(true);
    })();
  }, []);

  const symbols = useMemo(() => {
    const s = new Set(trades.map((t) => t.symbol).filter(Boolean));
    return ["All", ...Array.from(s).sort()];
  }, [trades]);
  const filteredTrades = useMemo(
    () =>
      trades.filter((t) => {
        const sideOk = sideFilter === "All" || t.side === sideFilter;
        const symOk = symbolFilter === "All" || t.symbol === symbolFilter;
        const fromOk = !dateFrom || t.dateStr >= dateFrom;
        const toOk = !dateTo || t.dateStr <= dateTo;
        return sideOk && symOk && fromOk && toOk;
      }),
    [trades, sideFilter, symbolFilter, dateFrom, dateTo]
  );

  const publishPublicSummary = async (allTrades) => {
    const dm = {};
    allTrades.forEach((t) => {
      dm[t.dateStr] = (dm[t.dateStr] || 0) + (t.netPnl || 0);
    });
    const daily = Object.entries(dm)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, pnl]) => ({ date, pnl }));
    const total = daily.reduce((s, r) => s + r.pnl, 0);
    const from_date = daily.length ? daily[0].date : null;
    const to_date = daily.length ? daily[daily.length - 1].date : null;
    const { error } = await supabase.from("public_journal_summary").upsert(
      {
        id: "main",
        daily,
        total,
        from_date,
        to_date,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "id" }
    );
    if (error) {
      console.error("Publish error:", error);
    }
  };

  // FIX: merge against the latest DB state (not stale React state) so a
  // partial daily import can never overwrite existing history.
  const handleImport = async (newTrades) => {
    setShowImport(false);

    // Always pull the current source of truth from Supabase first
    let dbTrades = [];
    try {
      const raw = await loadTradesFromSupabase();
      dbTrades = (raw || []).map((t) => ({
        ...t,
        date: new Date(t.date),
        entryTime: new Date(t.entryTime),
        exitTime: new Date(t.exitTime),
      }));
    } catch (e) {
      console.error("Import load failed, aborting to protect data:", e);
      alert("Could not load existing trades. Import cancelled. Please try again.");
      return;
    }

    const seen = new Set(dbTrades.map(tradeKey));
    const uniqueNew = newTrades.filter((t) => {
      const k = tradeKey(t);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });

    const merged = [...dbTrades, ...uniqueNew].map((t, i) => ({ ...t, id: i }));

    await saveTradestoSupabase(merged);
    await publishPublicSummary(merged);
    setTrades(merged);

    if (merged.length) {
      const last = merged.reduce((a, b) => (a.date > b.date ? a : b));
      setCalMonth(last.date.getMonth());
      setCalYear(last.date.getFullYear());
    }
  };

  const handleMonthChange = (d) => {
    let m = calMonth + d,
      y = calYear;
    if (m > 11) {
      m = 0;
      y++;
    }
    if (m < 0) {
      m = 11;
      y--;
    }
    setCalMonth(m);
    setCalYear(y);
  };
  const handleUpdate = async (id, f, v) => {
    // Optimistic UI update
    setTrades((p) => p.map((t) => (t.id === id ? { ...t, [f]: v } : t)));
    // FIX: persist against the latest DB state, not stale React state, so
    // editing a note can never wipe trades added since this tab loaded.
    try {
      const target = trades.find((t) => t.id === id);
      const targetKey = target ? tradeKey(target) : null;
      const raw = await loadTradesFromSupabase();
      const dbTrades = (raw || []).map((t) => ({
        ...t,
        date: new Date(t.date),
        entryTime: new Date(t.entryTime),
        exitTime: new Date(t.exitTime),
      }));
      const updated = dbTrades.map((t) =>
        tradeKey(t) === targetKey ? { ...t, [f]: v } : t
      );
      await saveTradestoSupabase(updated);
      await publishPublicSummary(updated);
    } catch (e) {
      console.error("handleUpdate save error:", e);
    }
  };

  // ✅ FIXED: Full stats computed from filteredTrades
  const stats = useMemo(() => {
    if (!filteredTrades.length) return null;
    const wins = filteredTrades.filter((t) => t.netPnl > 0);
    const losses = filteredTrades.filter((t) => t.netPnl <= 0);
    const totalPnl = d3.sum(filteredTrades, (t) => t.netPnl);
    const totalGross = d3.sum(filteredTrades, (t) => t.grossPnl);
    const totalComm = d3.sum(filteredTrades, (t) => t.commission);
    const avgWin = wins.length ? d3.mean(wins, (t) => t.netPnl) : 0;
    const avgLoss = losses.length
      ? Math.abs(d3.mean(losses, (t) => t.netPnl))
      : 0;
    const winRate = (wins.length / filteredTrades.length) * 100;
    const lossSum = d3.sum(losses, (t) => Math.abs(t.netPnl));
    const pf = lossSum > 0 ? d3.sum(wins, (t) => t.netPnl) / lossSum : Infinity;
    const rr = avgLoss > 0 ? avgWin / avgLoss : Infinity;
    const best = d3.max(filteredTrades, (t) => t.netPnl);
    const worst = d3.min(filteredTrades, (t) => t.netPnl);
    const dm = {};
    filteredTrades.forEach((t) => {
      dm[t.dateStr] = (dm[t.dateStr] || 0) + t.netPnl;
    });
    const dv = Object.values(dm);
    const totalDays = dv.length;
    const winDays = dv.filter((v) => v > 0).length;
    const avgDaily = totalDays ? totalPnl / totalDays : 0;
    let peak = 0,
      maxDD = 0,
      cum = 0;
    Object.entries(dm)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([, v]) => {
        cum += v;
        if (cum > peak) peak = cum;
        maxDD = Math.max(maxDD, peak - cum);
      });
    let streak = 0,
      maxW = 0,
      maxL = 0,
      currentStreak = 0;
    const sortedDays = Object.entries(dm).sort(([a], [b]) =>
      a.localeCompare(b)
    );
    sortedDays.forEach(([, v]) => {
      if (v > 0) {
        streak = streak > 0 ? streak + 1 : 1;
        maxW = Math.max(maxW, streak);
      } else {
        streak = streak < 0 ? streak - 1 : -1;
        maxL = Math.max(maxL, Math.abs(streak));
      }
    });
    currentStreak = streak;
    const bySymbol = {};
    filteredTrades.forEach((t) => {
      if (!bySymbol[t.symbol])
        bySymbol[t.symbol] = { pnl: 0, count: 0, wins: 0 };
      bySymbol[t.symbol].pnl += t.netPnl;
      bySymbol[t.symbol].count++;
      if (t.netPnl > 0) bySymbol[t.symbol].wins++;
    });
    return {
      totalPnl,
      totalGross,
      totalComm,
      totalTrades: filteredTrades.length,
      wins: wins.length,
      losses: losses.length,
      winRate,
      avgWin,
      avgLoss,
      pf,
      rr,
      best,
      worst,
      avgDaily,
      maxDD,
      totalDays,
      winDays,
      maxW,
      maxL,
      currentStreak,
      bySymbol,
    };
  }, [filteredTrades]);

  const tabs = [
    { id: "calendar", label: "Calendar" },
    { id: "stats", label: "Analytics" },
    { id: "log", label: "Trades" },
  ];
  const tabIcons = {
    calendar: (
      <Sv>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </Sv>
    ),
    stats: (
      <Sv>
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </Sv>
    ),
    log: (
      <Sv>
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" />
        <line x1="3" y1="12" x2="3.01" y2="12" />
        <line x1="3" y1="18" x2="3.01" y2="18" />
      </Sv>
    ),
  };

  // Journal UI (rendered inside PasswordGate)
  const journalUI = (logoutFn) => (
    <div
      style={{
        "--mono": "'JetBrains Mono',monospace",
        "--sans": "'DM Sans',system-ui,sans-serif",
        minHeight: "100vh",
        background: C.bg,
        color: C.text,
        fontFamily: "var(--sans)",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      {/* ✅ Header: no logo/text, just Import + Logout */}
      <header
        style={{
          padding: "12px 16px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 8,
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: C.bg,
        }}
      >
        <button
          onClick={() => setShowImport(true)}
          style={{
            background: `linear-gradient(135deg,${C.accent},${C.purple})`,
            border: "none",
            borderRadius: 8,
            color: "#fff",
            padding: "7px 13px",
            fontSize: 11,
            fontWeight: 800,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <Sv s={14}>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </Sv>{" "}
          Import
        </button>
        <button
          onClick={logoutFn}
          style={{
            ...btnIcon,
            padding: "7px 13px",
            fontSize: 11,
            fontWeight: 700,
            gap: 5,
            color: C.loss,
          }}
        >
          <Sv s={14}>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </Sv>{" "}
          Logout
        </button>
      </header>

      {/* Tabs */}
      <nav
        style={{
          display: "flex",
          gap: 2,
          padding: "6px 10px",
          borderBottom: `1px solid ${C.border}`,
          overflowX: "auto",
          position: "sticky",
          top: 55,
          zIndex: 99,
          background: C.bg,
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              background: tab === t.id ? C.accentSoft : "transparent",
              border:
                tab === t.id
                  ? `1px solid ${C.accentMid}`
                  : "1px solid transparent",
              borderRadius: 7,
              color: tab === t.id ? C.accent : C.textSec,
              padding: "7px 12px",
              fontSize: 11,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 5,
              transition: "all 0.15s",
              whiteSpace: "nowrap",
              flex: "1 1 auto",
              justifyContent: "center",
            }}
          >
            {tabIcons[t.id]} {t.label}
          </button>
        ))}
      </nav>

      <main
        style={{ padding: "14px 14px 90px", maxWidth: 880, margin: "0 auto" }}
      >
        {/* Filters */}
        {trades.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            <div style={{ flex: "1 1 180px", minWidth: 180 }}>
              <div
                style={{
                  fontSize: 9,
                  color: C.textDim,
                  textTransform: "uppercase",
                  letterSpacing: 1.3,
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                Side
              </div>
              <select
                value={sideFilter}
                onChange={(e) => setSideFilter(e.target.value)}
                style={{ ...inputSt, padding: "10px 12px" }}
              >
                <option value="All">All</option>
                <option value="Long">Long</option>
                <option value="Short">Short</option>
              </select>
            </div>
            <div style={{ flex: "1 1 180px", minWidth: 180 }}>
              <div
                style={{
                  fontSize: 9,
                  color: C.textDim,
                  textTransform: "uppercase",
                  letterSpacing: 1.3,
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                Instrument
              </div>
              <select
                value={symbolFilter}
                onChange={(e) => setSymbolFilter(e.target.value)}
                style={{ ...inputSt, padding: "10px 12px" }}
              >
                {symbols.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: "1 1 140px", minWidth: 140 }}>
              <div
                style={{
                  fontSize: 9,
                  color: C.textDim,
                  textTransform: "uppercase",
                  letterSpacing: 1.3,
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                From
              </div>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  ...inputSt,
                  padding: "10px 12px",
                  colorScheme: "dark",
                }}
              />
            </div>
            <div style={{ flex: "1 1 140px", minWidth: 140 }}>
              <div
                style={{
                  fontSize: 9,
                  color: C.textDim,
                  textTransform: "uppercase",
                  letterSpacing: 1.3,
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                To
              </div>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  ...inputSt,
                  padding: "10px 12px",
                  colorScheme: "dark",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                onClick={() => {
                  setSideFilter("All");
                  setSymbolFilter("All");
                  setDateFrom("");
                  setDateTo("");
                }}
                style={{
                  ...btnIcon,
                  padding: "10px 14px",
                  fontSize: 11,
                  fontWeight: 800,
                }}
              >
                Reset
              </button>
            </div>{" "}
          </div>
        )}

        {/* Stat cards */}
        {stats && (
          <div
            style={{
              display: "flex",
              gap: 7,
              flexWrap: "wrap",
              marginBottom: 14,
            }}
          >
            <Stat
              label="Net P&L"
              value={fmt(stats.totalPnl)}
              color={plC(stats.totalPnl)}
              sub={`${stats.totalTrades} trades · ${stats.totalDays} days`}
            />
            <Stat
              label="Win Rate"
              value={stats.winRate.toFixed(1) + "%"}
              color={stats.winRate >= 50 ? C.profit : C.loss}
              sub={`${stats.wins}W / ${stats.losses}L`}
            />
            <Stat
              label="Profit Factor"
              value={stats.pf === Infinity ? "∞" : stats.pf.toFixed(2)}
              color={stats.pf >= 1 ? C.profit : C.loss}
            />
            <Stat
              label="Avg Daily"
              value={fmtS(stats.avgDaily)}
              color={plC(stats.avgDaily)}
            />
            <Stat
              label="Current Streak"
              value={`${Math.abs(stats.currentStreak)}d`}
              color={
                stats.currentStreak > 0
                  ? C.profit
                  : stats.currentStreak < 0
                  ? C.loss
                  : C.textSec
              }
            />
          </div>
        )}

        {/* Calendar tab */}
        {tab === "calendar" && (
          <div
            style={{
              background: C.surface,
              borderRadius: 12,
              padding: 14,
              border: `1px solid ${C.border}`,
            }}
          >
            <CalendarHeatmap
              trades={filteredTrades}
              year={calYear}
              month={calMonth}
              onMonthChange={handleMonthChange}
              onDayClick={(ds) => setSelectedDay(ds)}
              onWeekClick={(dates) => setSelectedWeek(dates)}
            />
          </div>
        )}
        {selectedDay && (
          <DayDetailModal
            dateStr={selectedDay}
            trades={filteredTrades}
            onClose={() => setSelectedDay(null)}
          />
        )}
        {selectedWeek && (
          <WeekDetailModal
            weekDates={selectedWeek}
            trades={filteredTrades}
            onClose={() => setSelectedWeek(null)}
          />
        )}

        {/* ✅ FIXED: Analytics tab with full stats grid + strategy + symbols */}
        {tab === "stats" && stats && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <PerformanceChart trades={filteredTrades} />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))",
                gap: 7,
              }}
            >
              <Stat
                label="Gross P&L"
                value={fmt(stats.totalGross)}
                color={plC(stats.totalGross)}
              />
              <Stat
                label="Commissions"
                value={"-$" + stats.totalComm.toFixed(2)}
                color={C.loss}
              />
              <Stat
                label="Best Trade"
                value={fmt(stats.best)}
                color={C.profit}
              />
              <Stat
                label="Worst Trade"
                value={fmt(stats.worst)}
                color={C.loss}
              />
              <Stat
                label="Avg Win"
                value={fmt(stats.avgWin)}
                color={C.profit}
              />
              <Stat
                label="Avg Loss"
                value={fmt(-stats.avgLoss)}
                color={C.loss}
              />
              <Stat
                label="Risk:Reward"
                value={stats.rr === Infinity ? "∞" : `1:${stats.rr.toFixed(2)}`}
                color={C.accent}
              />
              <Stat
                label="Max Drawdown"
                value={fmt(-stats.maxDD)}
                color={C.loss}
              />
              <Stat
                label="Win Days"
                value={`${stats.winDays}/${stats.totalDays}`}
                sub={`${((stats.winDays / stats.totalDays) * 100).toFixed(0)}%`}
                color={C.profit}
              />
              <Stat
                label="Best Streak"
                value={stats.maxW + "d"}
                sub={`Loss: ${stats.maxL}d`}
                color={C.profit}
              />
              <Stat
                label="Long P&L"
                value={fmt(
                  filteredTrades
                    .filter((t) => t.side === "Long")
                    .reduce((s, t) => s + (t.netPnl || 0), 0)
                )}
                color={plC(
                  filteredTrades
                    .filter((t) => t.side === "Long")
                    .reduce((s, t) => s + (t.netPnl || 0), 0)
                )}
                sub={`${
                  filteredTrades.filter((t) => t.side === "Long").length
                } trades`}
              />
              <Stat
                label="Short P&L"
                value={fmt(
                  filteredTrades
                    .filter((t) => t.side === "Short")
                    .reduce((s, t) => s + (t.netPnl || 0), 0)
                )}
                color={plC(
                  filteredTrades
                    .filter((t) => t.side === "Short")
                    .reduce((s, t) => s + (t.netPnl || 0), 0)
                )}
                sub={`${
                  filteredTrades.filter((t) => t.side === "Short").length
                } trades`}
              />
            </div>
            {/* Hold Time Analysis */}
            {stats &&
              (() => {
                const withDur = filteredTrades.filter(
                  (t) => t.entryTime && t.exitTime
                );
                const durSec = (t) =>
                  (new Date(t.exitTime) - new Date(t.entryTime)) / 1000;
                const fmtDur = (s) => {
                  if (!s || isNaN(s)) return "—";
                  const m = Math.floor(s / 60);
                  const sec = Math.floor(s % 60);
                  return `${m}m ${String(sec).padStart(2, "0")}s`;
                };
                const all = withDur.length
                  ? withDur.reduce((s, t) => s + durSec(t), 0) / withDur.length
                  : 0;
                const wins = withDur.filter((t) => t.netPnl > 0);
                const losses = withDur.filter((t) => t.netPnl <= 0);
                const avgWin = wins.length
                  ? wins.reduce((s, t) => s + durSec(t), 0) / wins.length
                  : 0;
                const avgLoss = losses.length
                  ? losses.reduce((s, t) => s + durSec(t), 0) / losses.length
                  : 0;
                return (
                  <div
                    style={{
                      background: C.surface,
                      borderRadius: 12,
                      padding: 14,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div style={secLabel}>Average Hold Time</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <div
                        style={{
                          flex: "1 1 140px",
                          background: C.bg,
                          borderRadius: 8,
                          padding: "10px 14px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            color: C.textDim,
                            textTransform: "uppercase",
                            letterSpacing: 1.2,
                            fontWeight: 700,
                            marginBottom: 4,
                          }}
                        >
                          All Trades
                        </div>
                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 900,
                            color: C.accent,
                            fontFamily: "var(--mono)",
                          }}
                        >
                          {fmtDur(all)}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: C.textSec,
                            fontFamily: "var(--mono)",
                            marginTop: 2,
                          }}
                        >
                          {withDur.length} trades
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "1 1 140px",
                          background: C.bg,
                          borderRadius: 8,
                          padding: "10px 14px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            color: C.textDim,
                            textTransform: "uppercase",
                            letterSpacing: 1.2,
                            fontWeight: 700,
                            marginBottom: 4,
                          }}
                        >
                          Winning Trades
                        </div>
                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 900,
                            color: C.profit,
                            fontFamily: "var(--mono)",
                          }}
                        >
                          {fmtDur(avgWin)}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: C.textSec,
                            fontFamily: "var(--mono)",
                            marginTop: 2,
                          }}
                        >
                          {wins.length} trades
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "1 1 140px",
                          background: C.bg,
                          borderRadius: 8,
                          padding: "10px 14px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 9,
                            color: C.textDim,
                            textTransform: "uppercase",
                            letterSpacing: 1.2,
                            fontWeight: 700,
                            marginBottom: 4,
                          }}
                        >
                          Losing Trades
                        </div>
                        <div
                          style={{
                            fontSize: 20,
                            fontWeight: 900,
                            color: C.loss,
                            fontFamily: "var(--mono)",
                          }}
                        >
                          {fmtDur(avgLoss)}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            color: C.textSec,
                            fontFamily: "var(--mono)",
                            marginTop: 2,
                          }}
                        >
                          {losses.length} trades
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            {/* P&L by Day of Week */}
            {stats &&
              (() => {
                const days = [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                ];
                const dayData = [1, 2, 3, 4, 5].map((dow) => {
                  const dt = filteredTrades.filter((t) => {
                    const d =
                      t.entryTime instanceof Date
                        ? t.entryTime
                        : new Date(t.entryTime);
                    return d.getDay() === dow;
                  });
                  const pnl = dt.reduce((s, t) => s + (t.netPnl || 0), 0);
                  const wins = dt.filter((t) => t.netPnl > 0).length;
                  return {
                    name: days[dow - 1],
                    short: days[dow - 1].slice(0, 3),
                    pnl,
                    count: dt.length,
                    wins,
                  };
                });
                return (
                  <div
                    style={{
                      background: C.surface,
                      borderRadius: 12,
                      padding: 14,
                      border: `1px solid ${C.border}`,
                    }}
                  >
                    <div style={secLabel}>P&L by Day of Week</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {dayData.map((d) => (
                        <div
                          key={d.name}
                          style={{
                            flex: "1 1 100px",
                            background: C.bg,
                            borderRadius: 8,
                            padding: "10px 14px",
                            minWidth: 90,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 9,
                              color: C.textDim,
                              textTransform: "uppercase",
                              letterSpacing: 1.2,
                              fontWeight: 700,
                              marginBottom: 4,
                            }}
                          >
                            {d.name}
                          </div>
                          <div
                            style={{
                              fontSize: 18,
                              fontWeight: 900,
                              color: d.count > 0 ? plC(d.pnl) : C.textDim,
                              fontFamily: "var(--mono)",
                            }}
                          >
                            {d.count > 0 ? fmtS(d.pnl) : "—"}
                          </div>
                          <div
                            style={{
                              fontSize: 10,
                              color: C.textSec,
                              fontFamily: "var(--mono)",
                              marginTop: 2,
                            }}
                          >
                            {d.count}t ·{" "}
                            {d.count > 0
                              ? ((d.wins / d.count) * 100).toFixed(0)
                              : 0}
                            % WR
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            <div
              style={{
                background: C.surface,
                borderRadius: 12,
                padding: 14,
                border: `1px solid ${C.border}`,
              }}
            >
              <div style={secLabel}>By Symbol</div>
              {Object.entries(stats.bySymbol)
                .sort(([, a], [, b]) => b.pnl - a.pnl)
                .map(([sym, d]) => (
                  <div
                    key={sym}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 0",
                      borderBottom: `1px solid ${C.border}`,
                    }}
                  >
                    <div>
                      <span
                        style={{ fontSize: 13, fontWeight: 800, color: C.text }}
                      >
                        {sym}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: C.textDim,
                          marginLeft: 8,
                          fontFamily: "var(--mono)",
                        }}
                      >
                        {d.count} trades ·{" "}
                        {((d.wins / d.count) * 100).toFixed(0)}% WR
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        color: plC(d.pnl),
                        fontFamily: "var(--mono)",
                      }}
                    >
                      {fmt(d.pnl)}
                    </span>
                  </div>
                ))}
            </div>
            <div
              style={{
                background: C.surface,
                borderRadius: 12,
                padding: 14,
                border: `1px solid ${C.border}`,
              }}
            >
              <div style={secLabel}>By Strategy</div>
              <StrategyBreakdown trades={filteredTrades} />
            </div>
          </div>
        )}

        {/* Trades log tab */}
        {tab === "log" && (
          <div
            style={{
              background: C.surface,
              borderRadius: 12,
              padding: 14,
              border: `1px solid ${C.border}`,
            }}
          >
            <div style={{ ...secLabel, marginBottom: 10 }}>
              Trade Log ({filteredTrades.length})
            </div>
            <div style={{ marginBottom: 14 }}>
              {!showDelete ? (
                <button
                  onClick={() => setShowDelete(true)}
                  style={{
                    background: "transparent",
                    border: `1px solid rgba(255,77,106,0.3)`,
                    color: C.loss,
                    borderRadius: 8,
                    padding: "8px 16px",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Delete All Trade Data
                </button>
              ) : (
                <div
                  style={{
                    background: C.lossSoft,
                    border: `1px solid rgba(255,77,106,0.3)`,
                    borderRadius: 10,
                    padding: 16,
                    maxWidth: 400,
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 800,
                      color: C.loss,
                      marginBottom: 4,
                    }}
                  >
                    ⚠ Delete All Data
                  </div>
                  <div
                    style={{ fontSize: 11, color: C.textSec, marginBottom: 12 }}
                  >
                    This will permanently delete all trades and public summary.
                    Enter password to confirm.
                  </div>
                  <input
                    type="password"
                    placeholder="Enter password"
                    value={delPw}
                    onChange={(e) => {
                      setDelPw(e.target.value);
                      setDelErr("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (delPw !== APP_PASSWORD) {
                          setDelErr("Wrong password");
                          return;
                        }
                        (async () => {
                          setDeleting(true);
                          try {
                            await supabase.from("trades").delete().eq("id", 1);
                            await supabase
                              .from("public_journal_summary")
                              .delete()
                              .eq("id", "main");
                            setTrades([]);
                            setShowDelete(false);
                            setDelPw("");
                            setDelErr("");
                          } catch (e2) {
                            console.error(e2);
                            setDelErr("Error deleting data");
                          }
                          setDeleting(false);
                        })();
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "9px 12px",
                      borderRadius: 8,
                      boxSizing: "border-box",
                      border: `1px solid ${delErr ? C.loss : C.borderLight}`,
                      background: C.bg,
                      color: C.text,
                      outline: "none",
                      marginBottom: 8,
                    }}
                  />
                  {delErr && (
                    <div
                      style={{
                        fontSize: 11,
                        color: C.loss,
                        fontWeight: 700,
                        marginBottom: 8,
                      }}
                    >
                      {delErr}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={async () => {
                        if (delPw !== APP_PASSWORD) {
                          setDelErr("Wrong password");
                          return;
                        }
                        setDeleting(true);
                        try {
                          await supabase.from("trades").delete().eq("id", 1);
                          await supabase
                            .from("public_journal_summary")
                            .delete()
                            .eq("id", "main");
                          setTrades([]);
                          setShowDelete(false);
                          setDelPw("");
                          setDelErr("");
                        } catch (e2) {
                          console.error(e2);
                          setDelErr("Error deleting data");
                        }
                        setDeleting(false);
                      }}
                      disabled={deleting}
                      style={{
                        flex: 1,
                        padding: "9px 12px",
                        border: "none",
                        borderRadius: 8,
                        background: C.loss,
                        color: "#fff",
                        fontWeight: 800,
                        fontSize: 12,
                        cursor: "pointer",
                        opacity: deleting ? 0.5 : 1,
                      }}
                    >
                      {deleting ? "Deleting..." : "Confirm Delete"}
                    </button>
                    <button
                      onClick={() => {
                        setShowDelete(false);
                        setDelPw("");
                        setDelErr("");
                      }}
                      style={{
                        flex: 1,
                        padding: "9px 12px",
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        background: "transparent",
                        color: C.textSec,
                        fontWeight: 800,
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            <TradeLog trades={filteredTrades} onUpdate={handleUpdate} />
          </div>
        )}
      </main>
      {showImport && (
        <ImportModal
          onImport={handleImport}
          onClose={() => setShowImport(false)}
        />
      )}
    </div>
  );

  return (
    <>
      {/* ✅ Favicon */}
      <link rel="icon" href="/icon.png" type="image/png" />

      <a href="/">
        <img
          src="/header.png"
          alt="Millium Header"
          style={{
            width: "100%",
            height: "clamp(60px,10vw,100px)",
            objectFit: "contain",
            objectPosition: "center",
            background: "#000",
            borderRadius: 12,
            marginBottom: 0,
            marginTop: 5,
            display: "block",
            border: "none",
          }}
        />
      </a>
      <TickerTape />

      <Routes>
        <Route
          path="/"
          element={
            <div
              style={{
                "--mono": "'JetBrains Mono',monospace",
                "--sans": "'DM Sans',system-ui,sans-serif",
                minHeight: "100vh",
                background: C.bg,
                color: C.text,
                fontFamily: "var(--sans)",
              }}
            >
              <link
                href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700;800&display=swap"
                rel="stylesheet"
              />
              <div
                style={{
                  maxWidth: 720,
                  margin: "0 auto",
                  padding: "18px 14px 14px",
                }}
              >
                <PublicSection trades={tradesForPublic} />
              </div>
            </div>
          }
        />
        <Route
          path="/journal"
          element={
            <PasswordGate>{({ logout }) => journalUI(logout)}</PasswordGate>
          }
        />
        <Route
          path="/risk"
          element={
            <RiskPasswordGate>
              <RiskDashboard />
            </RiskPasswordGate>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Footer />
      <div
        style={{
          position: "fixed",
          bottom: 20,
          right: 20,
          display: "flex",
          gap: 8,
          zIndex: 100,
        }}
      >
        <a
          href="/journal"
          style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "8px 14px",
            color: C.textSec,
            textDecoration: "none",
            fontFamily: "var(--mono)",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          Journal
        </a>
        <a
          href="/risk"
          style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "8px 14px",
            color: C.textSec,
            textDecoration: "none",
            fontFamily: "var(--mono)",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          Risk
        </a>
      </div>
    </>
  );
}